import { useState, useMemo, useCallback, useEffect, useRef, forwardRef } from 'react';
import {
  Box,
  Flex,
  Image,
  Text,
  Badge,
  IconButton,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  useToast,
  Portal,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';
// MI-139 — grouped movements + the Display menu
import useDisplaySettings, {
  GROUPING_NONE, GROUPING_CATEGORY, GROUPING_ACCOUNT, GROUPING_TYPE,
  GROUPING_CURRENCY, GROUPING_GOAL, GROUPING_DAY, VIEW_BOARD,
} from '../../hooks/useDisplaySettings';
import useGroupedMovements from '../../hooks/useGroupedMovements';
import useNoteSearch, { noteCondition } from '../../hooks/useNoteSearch';
import useMovementRowStore from '../../hooks/useMovementRowStore';
import DisplayMenu from './DisplayMenu';
import GroupedMovements from './GroupedMovements';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import OnboardingEmptyState from '../onboarding/OnboardingEmptyState';
import { DOCK_H } from '../chat/AssistantWidget';
import i18n from '../../i18n';
import MovementDetailPanel from './MovementDetailPanel';
import MovementAlertIcon from './MovementAlertIcon';
import InlineCategoryPicker, { CategoryPill } from '../shared/InlineCategoryPicker';
import { isPrimaryTextRestricted, getAccountDisplay, getPrimaryText, GROUP_COLORS } from '../../utils/movementDisplay';
import { tradeLabel } from '../../utils/subAccounts';
import InlineTagPicker from '../shared/InlineTagPicker';
import LoadMoreButton from './LoadMoreButton';
import MergeMovementsModal from './MergeMovementsModal';
import SplitMovementModal from './SplitMovementModal';
import GroupMovementsModal from './GroupMovementsModal';
import { updateMovement, fetchCategories, fetchTags, fetchMovementById, getAccounts, groupMovements, getReferenceData } from '../../api/endpointsBackend';
import { LuFolder, LuTag, LuEllipsis, LuLayers, LuLock, LuTarget, LuChevronUp, LuCheck, LuArrowLeftRight } from 'react-icons/lu';
import { FilterBar } from '../shared/filters/FilterBar';
import { useFilters } from '../../contexts/FilterContext';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import { useConvertedTotal } from '../../hooks/useConvertedTotal';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { ConvertedAmountsProvider, useDisplayAmount, useSelectionTotals } from '../../contexts/ConvertedAmountsContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useGoalsView } from '../../hooks/useGoalsView';
import GoalIcon from '../goals/GoalIcon';
import GoalMark from '../goals/GoalMark';
import { attachMovementsToGoal } from '../../api/endpointsBackend';
import { useMovementsData } from '../../hooks/useMovementsData';
import {
  useResizablePanel, PANEL_WIDTH_VAR, PANEL_DEFAULT_WIDTH, PANEL_MIN_WIDTH,
} from '../../hooks/useResizablePanel';
import { useNarrowerThan } from '../../hooks/useNarrowerThan';
import { detailPanelMoved, usePublishDetailPanel } from '../../hooks/useDetailPanel';
import { isDateWithinRange, getDateRangeForDate } from '../../utils/dateUtils';
import { movementShapesLists } from '../../utils/movementEdits';
import { formatMoney } from '../../utils/formatters';
import { formatDate, formatDayShort, formatDateShort, formatDateTime } from '../../utils/intlFormatters';
import { formatRelativeTime } from '../../utils/statementHelpers';
import "./ListMovements.css";

import { cardSurface } from '../shared/Card';
import CompactMovementRow from '../shared/CompactMovementRow';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneMovements from '../phone/PhoneMovements';
import { TOOLBAR_ICON_BUTTON } from '../shared/toolbarButton';
// Bulk Category Picker for floating action bar
function BulkCategoryPicker({ categories, selectedMovements, onSelect }) {
  const { t } = useTranslation('movements');
  const [search, setSearch] = useState('');
  // Get the most common type among selected movements to filter categories
  const dominantType = useMemo(() => {
    const types = selectedMovements.map(m => m.type).filter(Boolean);
    if (types.length === 0) return null;
    const counts = {};
    types.forEach(t => { counts[t] = (counts[t] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  }, [selectedMovements]);
  const filtered = categories
    .filter(c => !dominantType || c.type === dominantType)
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <InputGroup size="sm" mb={2}>
        <InputLeftElement pointerEvents="none" h="32px"><SearchIcon color="redesign.text.muted" boxSize={3} /></InputLeftElement>
        <Input placeholder={t('list.search.placeholder')} value={search} onChange={e => setSearch(e.target.value)} h="32px" borderRadius="md" fontSize="sm" />
      </InputGroup>
      <Box maxH="200px" overflowY="auto">
        {filtered.map(cat => (
          <Flex key={cat.id} px={2} py={1.5} cursor="pointer" borderRadius="md" _hover={{ bg: "redesign.bg.hover" }} align="center" gap={2} onClick={() => onSelect(cat)}>
            <Text fontSize="sm">{cat.icon || '📁'}</Text>
            <Text fontSize="sm">{cat.name}</Text>
          </Flex>
        ))}
      </Box>
    </>
  );
}

// Bulk Tag Picker for floating action bar
function BulkTagPicker({ tags, selectedMovements, onToggle }) {
  const { t } = useTranslation('movements');
  const [search, setSearch] = useState('');
  // A tag is "checked" if ALL selected movements have it
  const tagPresence = useMemo(() => {
    const map = {};
    tags.forEach(tag => {
      const count = selectedMovements.filter(m =>
        (m.tag_movements || []).some(tm => (tm.tag?.id || tm.tag_id) === tag.id)
      ).length;
      map[tag.id] = count === selectedMovements.length ? 'all' : count > 0 ? 'some' : 'none';
    });
    return map;
  }, [tags, selectedMovements]);
  const filtered = tags.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <InputGroup size="sm" mb={2}>
        <InputLeftElement pointerEvents="none" h="32px"><SearchIcon color="redesign.text.muted" boxSize={3} /></InputLeftElement>
        <Input placeholder={t('list.search.placeholder')} value={search} onChange={e => setSearch(e.target.value)} h="32px" borderRadius="md" fontSize="sm" />
      </InputGroup>
      <Box maxH="200px" overflowY="auto">
        {filtered.map(tag => {
          const presence = tagPresence[tag.id] || 'none';
          return (
            <Flex key={tag.id} px={2} py={1.5} cursor="pointer" borderRadius="md" _hover={{ bg: "redesign.bg.hover" }} align="center" gap={2} onClick={() => onToggle(tag)}>
              <Checkbox size="sm" isChecked={presence === 'all'} isIndeterminate={presence === 'some'} pointerEvents="none" />
              <Box w="8px" h="8px" borderRadius="full" bg={tag.color || '#3182CE'} flexShrink={0} />
              <Text fontSize="sm" isTruncated>{tag.name}</Text>
            </Flex>
          );
        })}
      </Box>
    </>
  );
}

/**
 * Bulk "Set goal": single select, plus Remove goal. It loads the goals itself
 * so the page pays for them only when the bar is opened.
 */
function BulkGoalPicker({ accounts, onSelect, onRemove }) {
  const { t } = useTranslation('goals');
  const [search, setSearch] = useState('');
  const { rows, loading } = useGoalsView({ includeArchived: false });
  const accountName = (id) => accounts.find(a => a.id === id)?.name;
  const filtered = rows.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <InputGroup size="sm" mb={2}>
        <InputLeftElement pointerEvents="none" h="32px"><SearchIcon color="redesign.text.muted" boxSize={3} /></InputLeftElement>
        <Input placeholder={t('searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} h="32px" borderRadius="md" fontSize="sm" />
      </InputGroup>
      <Box maxH="240px" overflowY="auto" overflowX="hidden">
        {loading && <Text fontSize="sm" color="redesign.text.muted" px={2} py={1.5}>{t('loading')}</Text>}
        {/* The same two sections as the Goals page, so a goal is found where
            the reader last saw it. */}
        {['active', 'ready'].map(status => {
          const inSection = filtered.filter(g => g.status === status);
          if (inSection.length === 0) return null;
          return (
            <Box key={status} mb={1}>
              <Text fontSize="2xs" fontWeight={600} color="redesign.text.muted" px={2} pt={1} pb={0.5}>
                {t(`sections.${status}`)}
              </Text>
              {inSection.map(goal => (
                <Flex key={goal.id} px={2} py={1.5} cursor="pointer" borderRadius="md" _hover={{ bg: 'redesign.bg.hover' }} align="center" gap={2} onClick={() => onSelect(goal)}>
                  <GoalIcon goal={goal} size={20} />
                  <Box minW={0}>
                    <Text fontSize="sm" isTruncated>{goal.name}</Text>
                    <Text fontSize="2xs" color="redesign.text.muted" isTruncated>
                      {goal.summary
                        ? (goal.summary.is_achieved ? `${t('status.achieved')} · ` : `${Math.round(goal.summary.pct)}% · `)
                        : ''}
                      {goal.accounts.map(a => accountName(a.account_id) || a.name).filter(Boolean).join(', ')}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Box>
          );
        })}
      </Box>
      <Flex mt={1} pt={1} borderTop="1px solid" borderColor="redesign.border">
        <Flex as="button" type="button" px={2} py={1.5} w="100%" borderRadius="md" _hover={{ bg: 'redesign.bg.hover' }} align="center" gap={2} onClick={onRemove}>
          <CloseIcon boxSize="8px" color="redesign.text.muted" />
          <Text fontSize="sm" color="redesign.text.muted">{t('bulk.remove')}</Text>
        </Flex>
      </Flex>
    </>
  );
}

// Constants - No longer using fixed column widths, using flexbox instead


/**
 * An account's institution mark — its logo, or its own emoji when the source
 * has no asset. The same rule the Accounts page and the grouped headers follow.
 */
const AccountGlyph = ({ account }) => {
  const fallback = <Text fontSize="xs" lineHeight="1" flexShrink={0}>{account?.icon || '🏦'}</Text>;
  if (!account?.source_logo_url) return fallback;
  return (
    <Image
      src={account.source_logo_url}
      alt=""
      boxSize="13px"
      objectFit="contain"
      borderRadius="2px"
      flexShrink={0}
      fallback={fallback}
    />
  );
};

/**
 * Helper function to get tag names from movement
 */
const getTagNames = (movement) => {
  if (!movement.tag_movements || movement.tag_movements.length === 0) {
    return [];
  }
  return movement.tag_movements.map(tm => tm.tag.name);
};

/**
 * Copilot-style amount display with the currency code shown before the number.
 *
 * MI-318 — when a display currency is active, the row shows the converted value
 * instead, prefixed with `≈`. The badge that totals carry does not scale to a
 * list (one per row would be all chrome), and a single character keeps "this is
 * approximate" true on every line. Rows that could not be converted carry no
 * `≈`: those figures *are* exact, in their own currency.
 */
const CopilotAmount = ({ movement, amount, currency, type, size = "sm", isIgnored = false, showCurrency = true }) => {
  const display = useDisplayAmount(movement);
  const shown = movement ? display : { amount, currency, isConverted: false };
  const formattedNumber = formatMoney(shown.amount, shown.currency, true);
  currency = shown.currency;
  const color = type === 'Income' ? 'redesign.amount.income'
              : type === 'Expenses' ? 'redesign.amount.expense'
              : 'redesign.amount.neutral';

  return (
    <RateSourceTooltip sources={shown.rateSources} isDisabled={!shown.isConverted}>
    <Text
      // Tabular figures, so the decimal point lands on the same column down
      // the list instead of wandering with the digits above it.
      textStyle="tabularNumbers"
      fontSize={size}
      fontWeight="600"
      color={color}
      textAlign="right"
      whiteSpace="nowrap"
      minW={size === "xs" ? "auto" : "110px"}
      /* The converted value arrives a moment after the row does. Until it lands
         the figure is still in the movement's own currency and about to change,
         so it is dimmed rather than shown as if it were final — a number nobody
         should act on yet. */
      opacity={shown.isPending ? 0.35 : 1}
      transition="opacity 0.15s ease-out"
      // The amount is the part that no longer counts, so striking it
      // is the clearest signal that this row contributes nothing to a total.
      textDecoration={isIgnored ? 'line-through' : 'none'}
    >
      {shown.isConverted && (
        <Text as="span" fontSize="0.85em" mr="0.15em" color="currentColor" opacity={0.7}>≈</Text>
      )}
      {showCurrency && (
        <Text as="span" fontSize="0.72em" mr="0.25em" color="currentColor" opacity={0.75}>{currency}</Text>
      )}
      {formattedNumber}
    </Text>
    </RateSourceTooltip>
  );
};

/**
 * Inline tag picker - shows colored dots + count, click to toggle tags
 */
const VerifiedIcon = (props) => (
  <Box as="svg" viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

const CurrencyExchangeIcon = forwardRef((props, ref) => (
  <Box
    ref={ref}
    as="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 8h15" />
    <path d="M14 4l4 4-4 4" />
    <path d="M21 16H6" />
    <path d="M10 12l-4 4 4 4" />
  </Box>
));
CurrencyExchangeIcon.displayName = 'CurrencyExchangeIcon';

// MI-315 — counts the statements, which are what the detail panel's Origin
// lists and the reader can open; a count of bank rows named something they
// never see. A movement whose links list no file keeps the row count.
const LinkedTransactionsBadge = ({ movement }) => {
  const { t } = useTranslation('movements');
  const files = movement.source_files?.length || 0;
  const count = movement.num_transactions;
  return (
    <Tooltip
      label={files > 0
        ? t(files === 1 ? 'list.tooltips.sourceFilesOne' : 'list.tooltips.sourceFilesOther', { count: files })
        : t(count === 1 ? 'list.tooltips.linkedTransactionsOne' : 'list.tooltips.linkedTransactionsOther', { count })}
      fontSize="xs"
      hasArrow
      placement="top"
    >
      <Box color="redesign.accent.blue" flexShrink={0} cursor="default" w="12px" h="12px">
        <VerifiedIcon w="12px" h="12px" />
      </Box>
    </Tooltip>
  );
};

/**
 * Mobile card component for a single movement
 * New design: Note (bold) + Account (gray) on left, badges + amount on right
 * Grouped movements get additional indentation with nested border
 */
// MI-139: `isVisible` lets the grouped view's Display properties hide columns.
// Defaults to showing everything, so the ungrouped list is unaffected.
const SHOW_ALL = () => true;

/**
 * MI-315 — the list's selection checkbox: a 16px box with a round check, or a
 * dash while only part is selected. The rows and the select-all box share it,
 * so they always look alike. Its 28px hit area overhangs the box on every side
 * without taking room from the row.
 */
const CHECK_MARK = 'M2.5 6.3l2.3 2.3 4.7-5.1';
const DASH_MARK = 'M3 6h6';
const SelectionCheckbox = ({ isChecked = false, isIndeterminate = false, onChange, ariaLabel }) => {
  const filled = isChecked || isIndeterminate;
  return (
    <Box
      as="button"
      type="button"
      role="checkbox"
      aria-checked={isIndeterminate ? 'mixed' : isChecked}
      aria-label={ariaLabel}
      onClick={onChange}
      w="28px"
      h="28px"
      m="-6px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      borderRadius="6px"
      _focusVisible={{ boxShadow: 'outline' }}
    >
      <Box
        as="span"
        w="16px"
        h="16px"
        borderRadius="4px"
        border="1.5px solid"
        borderColor={filled ? 'redesign.accent.blue' : 'redesign.checkbox.border'}
        bg={filled ? 'redesign.accent.blue' : 'redesign.bg.surface'}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {filled && (
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={isIndeterminate ? DASH_MARK : CHECK_MARK} />
          </svg>
        )}
      </Box>
    </Box>
  );
};

/**
 * A movement on a narrow list: the same single line the side panels draw.
 *
 * This replaced a stacked card that put the account's institution logo under
 * the name. At phone widths those marks read as noise, and the panels' compact
 * row was already the layout that held up best in a narrow column. What the
 * card did beyond layout comes along — the selection checkbox, the group
 * marker and the alert sit in the row's leading slot; selection, highlight and
 * the ignored state live on the wrapper.
 */
const CompactStackedRow = ({ movement, onEditClick, isInGroup = false, groupColor = 'purple.400', isSelected = false, isHighlighted = false, onSelect = null, selectionActive = false, categories = [], onCategoryChange = null, allTags = [], onTagsChange = null, isVisible = SHOW_ALL }) => {
  const { t } = useTranslation('movements');
  const restricted = isPrimaryTextRestricted(movement);
  // See DesktopMovementRow — an ignored row stays visible, but its pickers are off.
  const inert = movement.is_ignored ? 'none' : undefined;

  const leading = (
    <Flex align="center" gap="6px">
      {selectionActive && (
        <Box onClick={(e) => e.stopPropagation()} display="flex" alignItems="center">
          <SelectionCheckbox
            isChecked={isSelected}
            onChange={(e) => { e.stopPropagation(); onSelect?.(movement.id); }}
            ariaLabel={t('list.bulkBar.selectRow')}
          />
        </Box>
      )}
      {isInGroup && <Box w="5px" h="5px" borderRadius="full" bg={groupColor} flexShrink={0} />}
      <MovementAlertIcon movement={movement} boxSize="0.7rem" />
    </Flex>
  );

  return (
    <Box
      id={`movement-${movement.id}`}
      px={2}
      borderRadius="4px"
      bg={isSelected || isHighlighted ? 'redesign.bg.accent' : 'transparent'}
      opacity={movement.is_ignored ? 0.55 : 1}
      transition="background-color 0.15s ease"
    >
      <CompactMovementRow
        leading={leading}
        note={getPrimaryText(movement)}
        subNote={tradeLabel(movement)}
        noteRestricted={restricted}
        struck={Boolean(movement.is_ignored)}
        restrictedLabel={t('list.fallback.restricted')}
        restrictedTooltip={t('list.tooltips.restricted')}
        accountName={isVisible('account') ? getAccountDisplay(movement) : undefined}
        tags={onTagsChange
          ? <Box pointerEvents={inert}><InlineTagPicker movement={movement} allTags={allTags} onTagsChange={onTagsChange} /></Box>
          : undefined}
        category={isVisible('category') && onCategoryChange
          ? <Box pointerEvents={inert}><InlineCategoryPicker movement={movement} categories={categories} onCategoryChange={onCategoryChange} size="2xs" maxW="100%" /></Box>
          : undefined}
        amount={movement.amount}
        currency={movement.currency}
        refund={movement.amount < 0 || movement.type === 'Income'}
        onClick={() => onEditClick(movement.id)}
      />
    </Box>
  );
};

/**
 * Mobile list component for displaying daily movements
 * Movements with movement_group_id get indented styling and are grouped together
 */
const MobileMovementList = ({ movements, onEditClick, selectedIds, onSelect, selectionActive, highlightedId, categories, onCategoryChange, allTags, onTagsChange, isVisible, hideMovementDate, hideCreatedDate, categoryAsIcon, hideReconciled, accountAsIcon }) => {
  // Group movements together while preserving order of first occurrence
  const groupedMovements = useMemo(() => {
    const result = [];
    const groupMap = new Map();
    const processedGroups = new Set();

    movements.forEach(mov => {
      if (mov.movement_group_id) {
        if (!groupMap.has(mov.movement_group_id)) {
          groupMap.set(mov.movement_group_id, []);
        }
        groupMap.get(mov.movement_group_id).push(mov);
      }
    });

    movements.forEach(mov => {
      if (mov.movement_group_id) {
        if (!processedGroups.has(mov.movement_group_id)) {
          processedGroups.add(mov.movement_group_id);
          groupMap.get(mov.movement_group_id).forEach(groupedMov => {
            result.push(groupedMov);
          });
        }
      } else {
        result.push(mov);
      }
    });

    return result;
  }, [movements]);

  // Assign alternating colors to different groups
  const groupColorMap = useMemo(() => {
    const map = new Map();
    let colorIndex = 0;
    groupedMovements.forEach(mov => {
      if (mov.movement_group_id && !map.has(mov.movement_group_id)) {
        map.set(mov.movement_group_id, GROUP_COLORS[colorIndex % GROUP_COLORS.length]);
        colorIndex++;
      }
    });
    return map;
  }, [groupedMovements]);

  return (
    <Box>
      {groupedMovements.map((mov, index) => (
        <CompactStackedRow
          key={mov.id || index}
          movement={mov}
          onEditClick={onEditClick}
          isInGroup={!!mov.movement_group_id}
          groupColor={groupColorMap.get(mov.movement_group_id)}
          isSelected={selectedIds?.has(mov.id)}
          isHighlighted={mov.id === highlightedId}
          onSelect={onSelect}
          selectionActive={selectionActive}
          categories={categories}
          onCategoryChange={onCategoryChange}
          allTags={allTags}
          onTagsChange={onTagsChange}
          isVisible={isVisible}
        />
      ))}
    </Box>
  );
};

/**
 * The account the row belongs to (MI-336).
 *
 * Its name while there is room for one; its institution's mark once the list
 * gets tight, where a column of "Bancolombia…" told the reader nothing the
 * logo doesn't. A transfer shows both ends either way.
 */
const RowAccount = ({ movement, isVisible, iconOnly }) => {
  if (!isVisible('account')) return null;
  const label = getAccountDisplay(movement);
  const isTransfer = movement.type === 'Transfer';

  // Directly after the name, wherever that ends — not a column of its own.
  // `flexShrink={0}` is what makes the *name* give way first when the row runs
  // out of room: a half-shown account name is a prefix every row shares.
  if (!iconOnly) {
    return (
      <Text
        fontSize="xs"
        color="redesign.text.muted"
        lineHeight="1.4"
        ml="2px"
        flexShrink={0}
        maxW={ACCOUNT_MAX_WIDTH}
        isTruncated
        title={label}
      >
        {label}
      </Text>
    );
  }

  return (
    <Tooltip label={label} fontSize="xs" hasArrow openDelay={300} placement="top">
      <Flex align="center" gap="3px" flexShrink={0} ml="2px">
        {isTransfer ? (
          <>
            <AccountGlyph account={movement.from_account} />
            <Text fontSize="2xs" color="redesign.text.faint" lineHeight="1">→</Text>
            <AccountGlyph account={movement.to_account} />
          </>
        ) : (
          <AccountGlyph account={movement.account} />
        )}
      </Flex>
    </Tooltip>
  );
};

/**
 * When the money moved (MI-336).
 *
 * The row used to have no date of its own: every row sat under a date header
 * that carried it. Ungrouped, there is no header, so the row has to. The chip
 * is honoured literally — on means on, day header above it or not — because a
 * toggle that silently does nothing under one grouping is what got this
 * property removed the first time.
 *
 * Rendered immediately before `created`, so the two read as a pair: when it
 * happened, then when Mi Kapital heard about it.
 */
const RowDate = ({ movement, isVisible }) => {
  const { t } = useTranslation('movements');
  if (!isVisible('date') || !movement.date) return null;
  return (
    <Tooltip
      label={t('list.row.dateTooltip', { date: formatDate(movement.date) })}
      openDelay={300} hasArrow fontSize="xs" placement="top"
    >
      <Text fontSize="2xs" color="redesign.text.muted" flexShrink={0} whiteSpace="nowrap">
        {formatDayShort(movement.date)}
      </Text>
    </Tooltip>
  );
};

/**
 * When the row entered Mi Kapital.
 *
 * Compact in the row and complete on hover, the way Linear does it: the date
 * is a glance, the full stamp is a question you only sometimes ask.
 */
const RowCreated = ({ movement, isVisible }) => {
  const { t } = useTranslation('movements');
  if (!isVisible('created') || !movement.created_at) return null;
  return (
    <Tooltip
      label={t('list.row.createdTooltip', { date: formatDateTime(movement.created_at) })}
      openDelay={300} hasArrow fontSize="xs" placement="top"
    >
      <Text fontSize="2xs" color="redesign.text.muted" flexShrink={0} whiteSpace="nowrap">
        {formatDateShort(movement.created_at)}
      </Text>
    </Tooltip>
  );
};

/**
 * Desktop row component for a single movement
 * New design: Blue left border, Note + Account on left, badges + amount on right
 * Grouped movements get additional indentation with nested border
 */
const DesktopMovementRow = ({ movement, onEditClick, isInGroup = false, groupColor = 'purple.400', isSelected = false, isHighlighted = false, onSelect = null, selectionActive = false, categories = [], onCategoryChange = null, allTags = [], onTagsChange = null, isVisible = SHOW_ALL, accountAsIcon = false, hideMovementDate = false, hideCreatedDate = false, categoryAsIcon = false, hideReconciled = false, hideGoalMark = false }) => {
  const { t } = useTranslation('movements');
  // A date column shows when the reader wants it *and* the row still has the
  // width for it — the two questions are separate, and so are the two dates.
  const showMovementDate = !hideMovementDate && isVisible('date');
  const showCreatedDate = !hideCreatedDate && isVisible('created');
  // A movement that no longer counts toward its goal keeps its mark at any
  // width the block still has, menu or not: the group header cannot say it.
  const showGoal = Boolean(movement.goal) && !hideGoalMark &&
    (isVisible('goal') || movement.goal.effect === 'invalid');
  const tagNames = getTagNames(movement);
  const isTransfer = movement.type === "Transfer";
  // A buy, sell or receive: "2 SPY @ 150.00" on a line under the name.
  const trade = tradeLabel(movement);

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onSelect?.(movement.id);
  };

  return (
    <Flex
      role="group"
      id={`movement-${movement.id}`}
      minH="38px"
      py="5px"
      px={3}
      _hover={{ bg: isHighlighted ? "redesign.bg.accent" : "redesign.bg.hover", cursor: "pointer" }}
      onClick={() => onEditClick(movement.id)}
      align="center"
      bg={isSelected ? "redesign.bg.accent" : isHighlighted ? "redesign.bg.accent" : "transparent"}
      // The open movement is marked on its left edge, so it stays findable
      // when other rows are selected too.
      boxShadow={isHighlighted ? "inset 2px 0 0 var(--chakra-colors-redesign-accent-blue)" : "none"}
      position="relative"
      transition="background-color 0.15s ease"
      // See MobileMovementCard — visible, interactive, but inactive.
      opacity={movement.is_ignored ? 0.55 : 1}
    >
      {/* Left side: Checkbox + Group indicator + Note + Account */}
      <Flex align="center" gap="8px" flex={1} minW={0}>
        {selectionActive && (
          <Box onClick={(e) => e.stopPropagation()} flexShrink={0} display="flex" alignItems="center">
            <SelectionCheckbox
              isChecked={isSelected}
              onChange={handleCheckboxClick}
              ariaLabel={t('list.bulkBar.selectRow')}
            />
          </Box>
        )}
        {isInGroup && (
          <Box w="6px" h="6px" borderRadius="full" bg={groupColor} flexShrink={0} />
        )}
        {isTransfer && (
          <Box as="span" color="redesign.text.muted" display="inline-flex" flexShrink={0}>
            <LuArrowLeftRight size={12} aria-hidden="true" />
          </Box>
        )}
        {/* Natural width up to the cap, and the first thing to give when the
            row runs short — so the account stays beside it instead of being
            pushed into a column of its own. */}
        <Flex align="center" gap={1.5} flex="0 1 auto" minW={0} maxW={NAME_MAX_WIDTH}>
          {isPrimaryTextRestricted(movement) && (
            <Tooltip label={t('list.tooltips.restricted')} fontSize="xs" hasArrow>
              <Box as="span" color="redesign.text.muted" display="inline-flex" flexShrink={0}>
                <LuLock size={12} />
              </Box>
            </Tooltip>
          )}
          {/* The name and its trade line stack in a box as wide as the longer
              of the two. Wrapped onto a second line of the row instead, the
              trade still counted toward the row's width as if it sat beside
              the name, and pushed the account that far past it. */}
          <Box minW={0}>
            <Text
              fontSize="md"
              fontWeight="500"
              color={isPrimaryTextRestricted(movement) ? 'redesign.text.muted' : 'redesign.heading'}
              fontStyle={isPrimaryTextRestricted(movement) ? 'italic' : 'normal'}
              // See MobileMovementCard — struck through when ignored.
              textDecoration={movement.is_ignored ? 'line-through' : 'none'}
              // `isTruncated`, not `noOfLines`: line-clamp breaks at word
              // boundaries, so a name whose next word doesn't fit ends well
              // short of the box — 43px short on one row and 4px on the next,
              // with the account after both. Cutting at the box edge instead
              // puts the account right after the name it belongs to.
              isTruncated
              lineHeight="1.4"
            >
              {isPrimaryTextRestricted(movement) ? t('list.fallback.restricted') : getPrimaryText(movement)}
            </Text>
            {trade && <Text fontSize="2xs" color="redesign.text.muted" isTruncated lineHeight="1.3">{trade}</Text>}
          </Box>
          {/* Badges and status icons ride inside the elastic column rather
              than after the account: out here their varying widths moved the
              account column a few pixels on every row that had one. In here
              they cost the name a few characters instead, and the columns to
              the right hold their line. */}
          {movement.is_ignored && (
            <Tooltip label={t('list.tooltips.ignored')} fontSize="xs" hasArrow>
              <Badge colorScheme="gray" variant="subtle" fontSize="0.55rem" borderRadius="full" px="4px" py={0} lineHeight="1.3" fontWeight="medium" flexShrink={0}>
                {t('list.badges.ignored')}
              </Badge>
            </Tooltip>
          )}
          <MovementAlertIcon movement={movement} boxSize="0.7rem" />
          {movement.currency_exchange && (
            <Tooltip
              label={t('list.tooltips.exchanged', { amount: formatMoney(movement.currency_exchange.value_target, movement.currency_exchange.currency_target, true), currency: movement.currency_exchange.currency_target })}
              fontSize="xs"
              hasArrow
            >
              <CurrencyExchangeIcon boxSize="0.8rem" color="redesign.text.muted" flexShrink={0} />
            </Tooltip>
          )}
        </Flex>
        <RowAccount movement={movement} isVisible={isVisible} iconOnly={accountAsIcon} />
      </Flex>

      {/* Right side: Transaction badge, Tags, Category badge, Amount */}
      {/* See MobileMovementCard — an ignored row's inline pickers are off. */}
      <Flex
        align="center"
        gap={2}
        flexShrink={0}
        pointerEvents={movement.is_ignored ? 'none' : undefined}
      >
        {/* Fixed from here to the amount, so the columns line up down the
            list. The tags and the category share one block, right-aligned: the
            chips end on a common edge and the tag pill keeps its place beside
            the chip rather than sitting in a lane of its own. */}
        {(onTagsChange || isVisible('category') || showGoal) && (
          <Flex
            w={categoryAsIcon ? TAGS_AND_CATEGORY_ICON_WIDTH : TAGS_AND_CATEGORY_WIDTH}
            justify="flex-end"
            align="center"
            gap={2}
            flexShrink={0}
            minW={0}
          >
            {showGoal && <GoalMark movement={movement} />}
            {onTagsChange && (
              <InlineTagPicker movement={movement} allTags={allTags} onTagsChange={onTagsChange} />
            )}
            {!isVisible('category') ? null : onCategoryChange ? (
              <InlineCategoryPicker
                movement={movement}
                categories={categories}
                onCategoryChange={onCategoryChange}
                maxW="100%"
                iconOnly={categoryAsIcon}
              />
            ) : !isTransfer && (
              <CategoryPill movement={movement} maxW="100%" iconOnly={categoryAsIcon} />
            )}
          </Flex>
        )}

        {/* The slot is held open whether or not this row is reconciled. It was
            dropped once, on the argument that the amount's own width kept the
            figures in line — true when the amount was the only column that had
            to. With a date and a created date beside it, a missing 12px here
            shifts every column left of it and the list reads ragged. */}
        {!hideReconciled && isVisible('reconciled') && (
          <Box w="12px" h="12px" flexShrink={0}>
            {movement.num_transactions > 0 && (
              <LinkedTransactionsBadge movement={movement} />
            )}
          </Box>
        )}

        {(showMovementDate || showCreatedDate) && (
          <Flex
            // Only as wide as what is left of the pair.
            w={showMovementDate && showCreatedDate
              ? DATES_BLOCK_WIDTH.both
              : DATES_BLOCK_WIDTH.one}
            justify="flex-end"
            align="center"
            gap={DATES_BLOCK_GAP}
            flexShrink={0}
          >
            {showMovementDate && <RowDate movement={movement} isVisible={isVisible} />}
            {showCreatedDate && <RowCreated movement={movement} isVisible={isVisible} />}
          </Flex>
        )}

        {/* Fixed, right-aligned: the figures already line up on their decimal
            point, and this keeps everything to their left lined up too. */}
        <Flex w={AMOUNT_COLUMN_WIDTH} justify="flex-end" flexShrink={0}>
          <CopilotAmount movement={movement} amount={movement.amount} currency={movement.currency} type={movement.type} isIgnored={movement.is_ignored} showCurrency={isVisible('currency')} />
        </Flex>
        {/* A new movement nobody has looked at yet: a dot after its amount,
            in the same place on every row, so the unseen ones read as a column. */}
        <Box w="6px" flexShrink={0} display="flex" justifyContent="center">
          {movement.is_new && (
            <Tooltip label={`${t('list.badges.new')} · ${t('list.tooltips.created', { relativeTime: formatRelativeTime(movement.created_at).toLowerCase() })}`} fontSize="xs" hasArrow>
              <Box as="span" w="6px" h="6px" borderRadius="full" bg="redesign.accent.blue" aria-label={t('list.badges.new')} role="img" />
            </Tooltip>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

/**
 * Desktop list component for displaying daily movements
 * Movements with movement_group_id get indented styling and are grouped together
 */
const DesktopMovementList = ({ movements, onEditClick, selectedIds, onSelect, selectionActive, highlightedId, categories, onCategoryChange, allTags, onTagsChange, isVisible, accountAsIcon, hideMovementDate, hideCreatedDate, categoryAsIcon, hideReconciled, hideGoalMark }) => {
  // Group movements together while preserving order of first occurrence
  const groupedMovements = useMemo(() => {
    const result = [];
    const groupMap = new Map(); // movement_group_id -> array of movements
    const processedGroups = new Set();

    // First pass: collect all grouped movements
    movements.forEach(mov => {
      if (mov.movement_group_id) {
        if (!groupMap.has(mov.movement_group_id)) {
          groupMap.set(mov.movement_group_id, []);
        }
        groupMap.get(mov.movement_group_id).push(mov);
      }
    });

    // Second pass: build result with groups at first occurrence
    movements.forEach(mov => {
      if (mov.movement_group_id) {
        // Only add the group once, at its first occurrence
        if (!processedGroups.has(mov.movement_group_id)) {
          processedGroups.add(mov.movement_group_id);
          // Add all movements from this group together
          groupMap.get(mov.movement_group_id).forEach(groupedMov => {
            result.push(groupedMov);
          });
        }
      } else {
        // Non-grouped movement, add as-is
        result.push(mov);
      }
    });

    return result;
  }, [movements]);

  // Assign alternating colors to different groups
  const groupColorMap = useMemo(() => {
    const map = new Map();
    let colorIndex = 0;
    groupedMovements.forEach(mov => {
      if (mov.movement_group_id && !map.has(mov.movement_group_id)) {
        map.set(mov.movement_group_id, GROUP_COLORS[colorIndex % GROUP_COLORS.length]);
        colorIndex++;
      }
    });
    return map;
  }, [groupedMovements]);

  return (
    <Box>
      {groupedMovements.map((mov, index) => (
        <DesktopMovementRow
          key={mov.id || index}
          movement={mov}
          onEditClick={onEditClick}
          isInGroup={!!mov.movement_group_id}
          groupColor={groupColorMap.get(mov.movement_group_id)}
          isSelected={selectedIds?.has(mov.id)}
          isHighlighted={mov.id === highlightedId}
          onSelect={onSelect}
          selectionActive={selectionActive}
          categories={categories}
          onCategoryChange={onCategoryChange}
          allTags={allTags}
          onTagsChange={onTagsChange}
          isVisible={isVisible}
          accountAsIcon={accountAsIcon}
          hideMovementDate={hideMovementDate}
          hideCreatedDate={hideCreatedDate}
          categoryAsIcon={categoryAsIcon}
          hideReconciled={hideReconciled}
          hideGoalMark={hideGoalMark}
        />
      ))}
    </Box>
  );
};

/**
 * Which shape a row is drawn in.
 *
 * One rule, and it reads the list's own width: the stacked card below
 * LIST_ROW_STACK_WIDTH, the desktop row above it. It used to be two rules
 * that disagreed — a viewport media query (`lg`) swapped both lists in and
 * out of `display: none` while the shedding ladder measured the container.
 * On a 900px window the ladder was busy trimming a row nobody could see,
 * and what the reader actually got was the stacked card: the account under
 * the name instead of beside it, and chips at a different x on every row,
 * because the card lays its right side out in natural flow.
 *
 * Both lists were also always mounted, so every row rendered twice.
 */
const MovementList = ({ movements, onEditClick, stacked = false, selectedIds, onSelect, selectionActive, highlightedId, categories, onCategoryChange, allTags, onTagsChange, isVisible, accountAsIcon = false, hideMovementDate = false, hideCreatedDate = false, categoryAsIcon = false, hideReconciled = false, hideGoalMark = false, onRendered }) => {
  const selectionProps = { selectedIds, onSelect, selectionActive, highlightedId, categories, onCategoryChange, allTags, onTagsChange, isVisible };

  // MI-315 — tells the page which rows are on screen, which is what "select
  // all" selects: a collapsed group's rows are loaded but not shown, and a
  // bulk action must never reach a row the reader cannot see.
  useEffect(() => {
    if (!onRendered) return undefined;
    const ids = movements.map(m => m.id);
    onRendered(ids, 1);
    return () => onRendered(ids, -1);
  }, [movements, onRendered]);
  const shedding = { accountAsIcon, hideMovementDate, hideCreatedDate, categoryAsIcon, hideReconciled, hideGoalMark };

  return stacked ? (
    <MobileMovementList movements={movements} onEditClick={onEditClick} {...shedding} {...selectionProps} />
  ) : (
    <DesktopMovementList movements={movements} onEditClick={onEditClick} {...shedding} {...selectionProps} />
  );
};

/** A figure of the selection, per currency: "≈ COP -10,000.00", or "—". */
const SelectionFigure = ({ label, figures }) => (
  <Flex align="baseline" gap={1.5}>
    {label && <Text fontSize="xs" fontWeight="500" color="redesign.text.muted">{label}</Text>}
    {figures.length === 0 ? (
      <Text fontSize="sm" fontWeight="700" color="redesign.text.muted">—</Text>
    ) : figures.map(({ currency, value, isConverted, rateSources }) => (
      <RateSourceTooltip key={currency} sources={rateSources} isDisabled={!isConverted}>
      <Flex align="baseline" gap={1}>
        <Text fontSize="2xs" fontWeight="600" letterSpacing="0.06em" color="redesign.text.muted">
          {isConverted ? `≈ ${currency}` : currency}
        </Text>
        <Text
          textStyle="tabularNumbers" fontSize="sm" fontWeight="700"
          color={value < 0 ? 'redesign.amount.expense'
            : value > 0 ? 'redesign.amount.income'
            : 'redesign.amount.neutral'}
        >
          {formatMoney(value, currency, true)}
        </Text>
      </Flex>
      </RateSourceTooltip>
    ))}
  </Flex>
);

/**
 * MI-315 — one figure of the selection beside the count, the way a spreadsheet
 * shows one: the Sum by default, or the Average, picked from the chip's menu.
 * How they are counted is `useSelectionTotals`'s rule; a selection with no
 * income or expense has no average, and one of ignored movements alone has
 * neither.
 */
const SelectionTotal = ({ movements }) => {
  const { t } = useTranslation('movements');
  const [stat, setStat] = useState('sum');
  const { totals, isPending } = useSelectionTotals(movements);
  const options = [
    {
      key: 'sum',
      label: t('list.bulkBar.sum'),
      help: t('list.bulkBar.sumHelp'),
      figures: totals.map(({ currency, total, isConverted, rateSources }) => ({
        currency, value: total, isConverted, rateSources,
      })),
    },
    {
      key: 'average',
      label: t('list.bulkBar.average'),
      help: t('list.bulkBar.averageHelp'),
      figures: totals
        .filter(({ count }) => count > 0)
        .map(({ currency, total, count, isConverted, rateSources }) => ({
          currency, value: total / count, isConverted, rateSources,
        })),
    },
  ];
  const chosen = options.find(o => o.key === stat);
  return (
    <Popover placement="top" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Button
              size="sm" variant="ghost" borderRadius="full" px={3} h="32px"
              _hover={{ bg: 'redesign.bg.card' }} _active={{ bg: 'redesign.bg.card' }}
              rightIcon={<LuChevronUp size={12} />} iconSpacing={1.5}
              opacity={isPending ? 0.35 : 1}
              aria-label={t('list.bulkBar.chooseFigure')}
            >
              <SelectionFigure label={chosen.label} figures={chosen.figures} />
            </Button>
          </PopoverTrigger>
          <PopoverContent w="300px" boxShadow="lg" borderRadius="xl" border="1px solid" borderColor="redesign.border">
            <PopoverBody p={0}>
              <Text px={4} py={2.5} fontSize="sm" fontWeight="600" color="redesign.heading" borderBottom="1px solid" borderColor="redesign.border">
                {t(movements.length === 1 ? 'list.bulkBar.selectionTitleOne' : 'list.bulkBar.selectionTitleOther', { count: movements.length })}
              </Text>
              <Box p={1.5}>
                {options.map(o => (
                  <Flex
                    key={o.key} as="button" type="button" w="100%" textAlign="left"
                    align="center" gap={3} px={2.5} py={2} borderRadius="lg"
                    bg={o.key === stat ? 'redesign.bg.accent' : 'transparent'}
                    _hover={{ bg: o.key === stat ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
                    onClick={() => { setStat(o.key); onClose(); }}
                    aria-pressed={o.key === stat}
                  >
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="600" color="redesign.text">{o.label}</Text>
                      <Text fontSize="xs" color="redesign.text.muted">{o.help}</Text>
                    </Box>
                    <SelectionFigure figures={o.figures} />
                    <Box w="14px" flexShrink={0} color="redesign.accent.blue">
                      {o.key === stat && <LuCheck size={14} />}
                    </Box>
                  </Flex>
                ))}
              </Box>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

/**
 * Main ListMovements Component
 * Refactored version with improved performance and maintainability
 */
const PANEL_CREATE_MODE = 'new';

/**
 * How the page gives ground as the window narrows (MI-336).
 *
 * The order is deliberate: the *panel* yields first, because it is the thing
 * the reader can already resize and the list is the page. Only once the panel
 * is at its own minimum does the list start shedding, and it sheds by how much
 * each column is worth — the secondary dates before the category's name,
 * the category's name before its colour, and the whole row's shape last. The
 * movement's name and its amount survive every step.
 *
 * All four are read off the list's own width, not the window's: the panel is
 * draggable, so the same window can leave the list any of these widths.
 */
const LIST_ROW_STACK_WIDTH = 500;

/**
 * Below this the account column drops its name for the institution's mark —
 * the text would only truncate to a prefix every row shares (MI-336).
 *
 * Set where the name column still has ~300px left after the account's 150px:
 * past that the two are fighting over the same space and the mark says more
 * per pixel than "Bancolombia…" does. Deliberately not a round 840, which is
 * exactly what a 1600px window leaves beside a default-width panel — a
 * threshold should not land on a common setup's exact width.
 */
const LIST_ACCOUNT_ICON_WIDTH = 800;

/**
 * The dates are the first thing worth losing, and they go one at a time.
 *
 * When the row entered Mi Kapital is import context; when the money moved is
 * the fact. So `created` — which is off by default, and therefore only ever on
 * because the reader asked for it — goes first, and the movement's own date
 * holds on a little longer. It is also the one that turns *itself* on in the
 * ungrouped view, where no header carries the date.
 */
const LIST_HIDE_CREATED_DATE_WIDTH = 740;
const LIST_HIDE_MOVEMENT_DATE_WIDTH = 700;

/**
 * Then the reconciled mark: whether a row is backed by a bank transaction is
 * worth knowing, but less than what the movement *is* — and the alert icon,
 * which flags the rows that need attention, rides with the name and stays.
 */
const LIST_HIDE_RECONCILED_WIDTH = 650;

/**
 * Then the goal mark, which is off by default and repeats what the goal's own
 * page says. It goes before the category's name, because the category is on
 * every row and the mark is on the few that belong to a goal.
 */
const LIST_HIDE_GOAL_WIDTH = 620;

/** Then the category keeps its colour and its icon but loses its name. */
const LIST_CATEGORY_ICON_WIDTH = 600;

/** The bulk bar's action names show from tablet width up; below, icons only. */
const BULK_LABEL_DISPLAY = { base: 'none', md: 'inline' };
const BULK_ICON_SPACING = { base: 0, md: 2 };

/** What the list needs before the panel is asked to give anything up. */
const LIST_MIN_WIDTH = 420;

/**
 * Below this the two cannot both be had: the list would be narrower than a
 * stacked row reads at while the panel sits at its floor. Whichever the reader
 * is actually looking at takes the width — the panel when a movement is open,
 * the list when none is.
 */
const ROW_SPLIT_MIN_WIDTH = LIST_MIN_WIDTH + PANEL_MIN_WIDTH;

/**
 * What the page needs before an *empty* panel is worth its column.
 *
 * A placeholder costs the same width as a movement and says nothing, so it is
 * held to a stricter test than an open one: it may not push the list into
 * shedding a single column. Below this the panel stands down entirely and the
 * list takes the page, which is also the state the reader gets back the
 * moment they close a movement on a narrow window.
 */
const PLACEHOLDER_MIN_WIDTH = LIST_ACCOUNT_ICON_WIDTH + PANEL_MIN_WIDTH;


/** Ascending, read back in the same order. */
const ROW_SPLIT_THRESHOLDS = [ROW_SPLIT_MIN_WIDTH, PLACEHOLDER_MIN_WIDTH];

/** Read in the order the hook returns them. */
const LIST_ROW_COLUMN_STEPS = [
  LIST_ROW_STACK_WIDTH,
  LIST_CATEGORY_ICON_WIDTH,
  LIST_HIDE_GOAL_WIDTH,
  LIST_HIDE_RECONCILED_WIDTH,
  LIST_HIDE_MOVEMENT_DATE_WIDTH,
  LIST_HIDE_CREATED_DATE_WIDTH,
  LIST_ACCOUNT_ICON_WIDTH,
];

/**
 * Where the movement's name stops growing.
 *
 * The account reads as part of the name, not as a column of its own — it sits
 * directly after it, wherever that lands. So the name is what has to stop:
 * past this it buys the reader nothing and only pushes the account away from
 * the row it belongs to.
 */
const NAME_MAX_WIDTH = "420px";

/** Wide enough for the longest COP figure the list shows, converted marker
 *  and currency code included. */
const AMOUNT_COLUMN_WIDTH = { base: "120px", md: "150px" };

/**
 * The dates share one right-aligned block, snug against each other.
 *
 * Two columns of their own, each sized for the rare "Ago 29, 2025", left
 * ~40px of air *between* two dates that belong side by side — the width was
 * paid on every row for a case almost none of them have. Sized instead for
 * one year-bearing date plus one short one (a `created` stamp is always
 * recent), the slack collects to the left of the pair, where it reads as the
 * gap between columns it already is.
 */
const DATES_BLOCK_WIDTH = { one: "58px", both: "106px" };

/** Between the two dates: enough to read as two values, far less than the
 *  ~40px that two separately-sized columns used to put between them. */
const DATES_BLOCK_GAP = "14px";

/**
 * Tags and category share one block, right-aligned inside it.
 *
 * One block rather than two columns: the tag pill belongs beside its category
 * chip, not in a lane of its own, and pinning only the block's right edge is
 * what lets the chips line up while the pill stays where it reads best.
 */
const TAGS_AND_CATEGORY_WIDTH = "190px";

/** The same block once the category is down to its icon. */
const TAGS_AND_CATEGORY_ICON_WIDTH = "84px";

/** As much account name as is worth reading before it, too, is just a prefix
 *  every row shares. */
const ACCOUNT_MAX_WIDTH = "170px";

/**
 * MI-318 — the provider wraps the list so every row can ask for its converted
 * value. Kept as a thin outer component so the list itself is unchanged: with
 * no display currency selected the provider fetches nothing at all.
 */
export default function ListMovements() {
  // The phone has its own Movements tab; the desktop list never mounts there.
  return useIsPhone() ? <PhoneMovements /> : (
    <ConvertedAmountsProvider>
      <ListMovementsInner />
    </ConvertedAmountsProvider>
  );
}

function ListMovementsInner() {
  const { t } = useTranslation('movements');
  const navigate = useNavigate();
  // Get global filter state
  const {
    dateRange,
    selectedFilters,
    filterGroups,
    groupsOperator,
    isAdvancedMode,
    filterOptions,
    filterOptionsLoading,
    apiFilters,
    apiFilterGroups,
    timePresets,
    addFilter,
    appendFilter,
    applySuggestion,
    removeFilter,
    removeLastFilter,
    fetchSuggestions,
    applyAiFilter,
    applyAiPeriod,
    updateFilter,
    clearFilters,
    navigatePeriod,
    setDateMode,
    goToToday,
    setCustomRange,
    enableAdvancedMode,
    disableAdvancedMode,
    addFilterGroup,
    removeFilterGroup,
    toggleGroupOperator,
    toggleGroupsOperator,
    addGroupCondition,
    removeGroupCondition,
    setDateRange
  } = useFilters();
  // MI-326 — the toolbar search applies as you type: once the typing pauses,
  // it is a note condition on every query below, grouped or not. Everything
  // here reads `queryFilters`; `apiFilters` is only the chips.
  const {
    searchText, setSearchText, queryFilters, clear: clearSearch,
  } = useNoteSearch(apiFilters);
  const { can } = useWorkspace();
  const canCreate = can('movement.create');

  // MI-139 — Display settings live in their own hook, deliberately apart from
  // FilterContext: Filter picks which movements are in the set, Display picks
  // how they are presented.
  const goalsEnabled = useGoalsEnabled();
  const display = useDisplaySettings({ goalsEnabled });
  // Group headers show each goal's own progress, so they need the summaries —
  // fetched only while one of the two levels actually groups by goal.
  const { rows: goalsForHeaders, refetch: refetchGoals } = useGoalsView({
    enabled: goalsEnabled
      && (display.grouping === GROUPING_GOAL || display.subgrouping === GROUPING_GOAL),
  });

  // MI-139 rev 5 — the page's index of loaded rows. In the grouped view the
  // rows live inside their groups, and `movementsList` (the flat query, off
  // while grouping) can no longer answer "which movement is id 42?" for the
  // merge modal, the bulk actions or the detail panel.
  const rowStore = useMovementRowStore();
  const clearRowStore = rowStore.clear;
  // The currencies the workspace actually uses — one of the lists "Show empty
  // groups" needs when the grouping is currency.
  const { activeCurrencies } = useDisplayCurrency();

  // MI-298/MI-318: the filtered set's converted figures. The grand total is no
  // longer rendered here — the group headers carry what matters — but the
  // payload still supplies the display currency and the unconvertible
  // disclosure, which is the one thing the headers cannot say.
  const { data: convertedTotal, displayCurrency } =
    useConvertedTotal(queryFilters, {
      startDate: dateRange.start_date,
      endDate: dateRange.end_date,
      groups: apiFilterGroups,
      groupsOperator,
    });

  // Capture movement_id from URL immediately (before FilterContext may rewrite the URL)
  const initialMovementIdRef = useRef(() => {
    const params = new URLSearchParams(window.location.search);
    const id = Number(params.get('movement_id'));
    return id > 0 ? id : null;
  });
  const initialMovementId = initialMovementIdRef.current();

  // Local state (not global)
  const [selectedMovementId, setSelectedMovementId] = useState(null);

  /**
   * Enter pins the search as a filter chip: the same condition typing already
   * applies, made to outlast the field — it survives navigation and travels in
   * the URL.
   */
  const handleSearchSubmit = useCallback((text) => {
    addFilter(noteCondition(text));
    clearSearch();
  }, [addFilter, clearSearch]);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  // MI-139 rev 6 — only to decorate group headers. The aggregate rows carry
  // ids and names, and the icon/colour live on the entity.
  const [accounts, setAccounts] = useState([]);

  // Fetch categories and tags for inline pickers
  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
    fetchTags().then(setAllTags).catch(() => {});
    getAccounts().then(setAccounts).catch(() => {});
  }, []);

  /**
   * What a category or account group shows besides its name.
   *
   * Resolved here rather than asked of the backend: the page already holds
   * both lists for its pickers, so a header costs a map lookup instead of
   * widening the aggregate payload.
   */
  /**
   * Which values a grouping *could* show, so "Show empty groups" can name the
   * ones the query returned nothing for.
   *
   * Dates are absent on purpose: their missing buckets come from the filter's
   * range, which the hook already has. These are facts about the workspace —
   * the categories that exist, the accounts still open — and only the page
   * knows them.
   */
  const emptyCandidatesFor = useCallback((dimension) => {
    if (dimension === GROUPING_CATEGORY) {
      return categories.map(c => ({ value: c.id, label: c.name }));
    }
    if (dimension === GROUPING_ACCOUNT) {
      // Closed accounts stay out: an account you shut is not a gap in the
      // period, and listing it every time would bury the ones still in use.
      return accounts
        .filter(a => a.is_active !== false)
        .map(a => ({ value: a.id, label: a.name }));
    }
    if (dimension === GROUPING_GOAL) {
      return goalsForHeaders.map(g => ({ value: g.id, label: g.name }));
    }
    if (dimension === GROUPING_TYPE) {
      return ['Income', 'Expenses', 'Transfer'].map(v => ({ value: v, label: v }));
    }
    if (dimension === GROUPING_CURRENCY) {
      return activeCurrencies.map(c => ({ value: c, label: c }));
    }
    return null;
  }, [categories, accounts, activeCurrencies, goalsForHeaders]);

  const groupMeta = useMemo(() => {
    const categoryById = new Map(categories.map(c => [String(c.id), c]));
    const accountById = new Map(accounts.map(a => [String(a.id), a]));
    const goalById = new Map(goalsForHeaders.map(g => [String(g.id), g]));
    return (group) => {
      if (group?.value == null) return null;
      if (group.grouping === GROUPING_CATEGORY) {
        const category = categoryById.get(String(group.value));
        if (!category) return null;
        return { icon: category.icon || null, color: category.color || null };
      }
      if (group.grouping === GROUPING_GOAL) {
        const goal = goalById.get(String(group.value));
        if (!goal) return null;
        // The goal's own figure over its whole life, not the net of the rows
        // under it: a goal funded by transfers would read 0.
        return {
          node: <GoalIcon goal={goal} size={16} />,
          barColor: goal.color,
          summary: goal.summary && {
            pct: goal.summary.pct,
            saved: goal.summary.saved,
            target: goal.summary.target,
            currency: goal.summary.currency,
          },
        };
      }
      if (group.grouping === GROUPING_ACCOUNT) {
        const account = accountById.get(String(group.value));
        if (!account) return null;
        // The institution's logo when there is one, the account's own emoji
        // otherwise — the rule the Accounts page already follows.
        return { icon: account.icon || '🏦', logo: account.source_logo_url || null };
      }
      return null;
    };
  }, [categories, accounts, goalsForHeaders]);

  // Multi-select state
  const toast = useToast();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { isOpen: isMergeOpen, onOpen: onMergeOpen, onClose: onMergeClose } = useDisclosure();
  const { isOpen: isSplitOpen, onOpen: onSplitOpen, onClose: onSplitClose } = useDisclosure();
  const [splitMovement, setSplitMovement] = useState(null);
  const { isOpen: isGroupOpen, onOpen: onGroupOpen, onClose: onGroupClose } = useDisclosure();


  // Always show checkboxes so users can start selecting
  const selectionActive = true;

  const handleToggleSelect = useCallback((movementId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(movementId)) {
        next.delete(movementId);
      } else {
        next.add(movementId);
      }
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // MI-315 — the rows on screen, counted, because a movement can be drawn under
  // two groups at once. A ref like the row store's: every group mounting would
  // otherwise re-render the whole page.
  const renderedRowsRef = useRef(new Map());
  const handleRowsRendered = useCallback((ids, delta) => {
    const rendered = renderedRowsRef.current;
    ids.forEach((id) => {
      const count = (rendered.get(id) || 0) + delta;
      if (count > 0) rendered.set(id, count);
      else rendered.delete(id);
    });
  }, []);

  // "Select all" the way a mail client does it: nothing selected selects every
  // row on screen, anything selected clears the selection.
  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds(prev => (prev.size > 0 ? new Set() : new Set(renderedRowsRef.current.keys())));
  }, []);

  // MI-336 — the ungrouped list, windowed by rows so its ordering can run
  // over the whole filtered set instead of over the dates that happen to be
  // loaded.
  const {
    movementsList,
    loading,
    error,
    paginationInfo,
    hasMore,
    loadMore,
    loadingMore,
    refetch,
    patchMovements,
  } = useMovementsData({
    dateRange,
    filters: queryFilters,
    filterGroups: apiFilterGroups,
    groupsOperator,
    ordering: display.ordering,
    displayCurrency,
    // Skip the flat-list query entirely while a grouping is active — its
    // result is never rendered there.
    enabled: !display.isGrouped,
  });

  /**
   * One write, both readers (rev 5). `patchMovements` only reaches the flat
   * list's state; a grouped row lives in the store, so an inline category
   * change used to need a reload before it showed.
   */
  const patchRowStore = rowStore.patch;
  const patchRows = useCallback((ids, changes) => {
    patchMovements(ids, changes);
    patchRowStore(ids, changes);
  }, [patchMovements, patchRowStore]);

  // MI-139 — group headers. Only fetched while a grouping is active; the flat
  // table keeps using useMovementsData untouched.
  const {
    groups: movementGroups,
    swimlanes: movementSwimlanes,
    loading: groupsLoading,
    currencyCount,
    hasMoreGroups,
    loadingMore: groupsLoadingMore,
    loadMoreGroups,
    refetch: refetchGroups,
    refreshHeaders: refreshGroupHeaders,
    generation: groupsGeneration,
    groupVersions,
  } = useGroupedMovements({
    enabled: display.isGrouped,
    grouping: display.grouping,
    subgrouping: display.subgrouping,
    ordering: display.ordering,
    showEmptyGroups: display.showEmptyGroups,
    emptyCandidatesFor,
    filters: queryFilters,
    filterGroups: apiFilterGroups,
    groupsOperator,
    startDate: dateRange.start_date,
    endDate: dateRange.end_date,
  });

  // MI-315 — the detail panel paints nothing until the reference lists are in
  // (MI-311), and only the first open in a workspace fetches them, /accounts
  // being the slowest endpoint we have. Warm that cache once the list is on
  // screen, so the first click opens as fast as every later one.
  const listOnScreen = display.isGrouped ? !groupsLoading : !loading;
  useEffect(() => {
    if (listOnScreen) getReferenceData().catch(() => {});
  }, [listOnScreen]);

  // The size of the filtered set. Ungrouped, the search answers it directly;
  // grouped, the loaded group headers add up to it — but only once every group
  // is in, or the number would silently understate what matched.
  const matchedCount = useMemo(() => {
    if (!display.isGrouped) {
      return paginationInfo?.totalInRange || null;
    }
    if (groupsLoading || hasMoreGroups || !movementGroups?.length) return null;
    return movementGroups.reduce((sum, group) => sum + (group.count ?? 0), 0) || null;
  }, [display.isGrouped, paginationInfo, groupsLoading, hasMoreGroups, movementGroups]);


  /**
   * Patching the row keeps the chip honest, but if the field just edited is
   * what the view groups *by*, the headers above it now lie — the count, the
   * total and the group the row is sitting in. Re-read the aggregates in that
   * one case; a group keeps its open state, since its key does not change.
   */
  /**
   * Re-read the grouped view from scratch: new headers, and every open group
   * re-hydrates its window off the new group objects. For changes a patch
   * cannot describe — a merge, a split — where rows appear or disappear.
   */
  const refreshGroupedView = useCallback(() => {
    if (!display.isGrouped) return;
    clearRowStore();
    refetchGroups();
  }, [display.isGrouped, clearRowStore, refetchGroups]);

  const refreshGroupsIfDimension = useCallback((dimension) => {
    if (!display.isGrouped) return;
    if (display.grouping === dimension || display.subgrouping === dimension) {
      refreshGroupHeaders();
    }
  }, [display.isGrouped, display.grouping, display.subgrouping, refreshGroupHeaders]);

  /**
   * MI-336 — how many currencies the ungrouped list is showing.
   *
   * The grouped view counts them for free: its aggregate returns one row per
   * currency. The flat list runs no aggregate, so it asks for the cheapest
   * possible one — a single dimension, a handful of rows — and only when the
   * answer can change anything: converting already lifts the amount gate, and
   * a display currency is what the page normally opens on.
   */
  const [flatCurrencyCount, setFlatCurrencyCount] = useState(1);
  useEffect(() => {
    if (display.isGrouped || displayCurrency) return undefined;
    const controller = new AbortController();
    groupMovements(
      queryFilters, ['currency'], dateRange.start_date, dateRange.end_date,
      apiFilterGroups, groupsOperator, null, { signal: controller.signal },
    )
      .then(rows => setFlatCurrencyCount(Array.isArray(rows) ? rows.length : 1))
      .catch(() => { /* the gate falls back to "one currency" — see below */ });
    return () => controller.abort();
  }, [display.isGrouped, displayCurrency, queryFilters, apiFilterGroups,
      groupsOperator, dateRange.start_date, dateRange.end_date]);

  // Whichever view is on screen is the one whose currencies the gate is about.
  const visibleCurrencyCount = display.isGrouped ? currencyCount : flatCurrencyCount;

  // Amount ordering is meaningless across currencies; if the visible set turns
  // out to hold more than one, step the ordering back rather than showing a
  // ranking by unit size.
  const reconcileOrdering = display.reconcileOrdering;
  useEffect(() => {
    reconcileOrdering(visibleCurrencyCount, { converting: Boolean(displayCurrency) });
  }, [visibleCurrencyCount, reconcileOrdering, displayCurrency]);

  // The store is a view of what is on screen: a new filter, a new grouping or
  // a new range makes every row in it suspect. Keyed on the serialized shape
  // rather than on the arrays themselves — those are rebuilt on every render,
  // and clearing bumps state, which would loop.
  const rowStoreKey = useMemo(() => JSON.stringify([
    queryFilters, apiFilterGroups, groupsOperator,
    dateRange.start_date, dateRange.end_date,
    display.grouping, display.subgrouping,
  ]), [queryFilters, apiFilterGroups, groupsOperator, dateRange.start_date,
       dateRange.end_date, display.grouping, display.subgrouping]);
  useEffect(() => {
    clearRowStore();
  }, [clearRowStore, rowStoreKey]);

  /**
   * "Open as list" — leave the grouped view and carry the group across as an
   * ordinary filter chip. This is the one deliberate point of contact between
   * the Display menu and the Filter bar.
   */



  const handleCategoryChange = useCallback(async (movementId, category) => {
    try {
      await updateMovement(movementId, { category_id: category.id });
      patchRows([movementId], { category, category_id: category.id });
      refreshGroupsIfDimension(GROUPING_CATEGORY);
      toast({ title: t('list.toasts.categoryUpdated', { name: category.name }), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('list.toasts.categoryUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [patchRows, refreshGroupsIfDimension, toast, t]);

  const handleTagsChange = useCallback(async (movementId, newTagIds, tags) => {
    try {
      await updateMovement(movementId, { tag_ids: newTagIds });
      const newTagMovements = newTagIds.map(id => {
        const tag = tags.find(t => t.id === id);
        return { tag_id: id, movement_id: movementId, tag };
      });
      patchRows([movementId], { tag_movements: newTagMovements });
    } catch {
      toast({ title: t('list.toasts.tagsUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [patchRows, toast, t]);

  const handleMergeComplete = useCallback((primaryId, primaryDate) => {
    setSelectedIds(new Set());
    if (primaryId) {
      setSelectedMovementId(primaryId);
      pendingScrollId.current = primaryId;
    }
    if (primaryDate && !isDateWithinRange(primaryDate, dateRange)) {
      setDateRange(getDateRangeForDate(primaryDate, dateRange.mode));
    } else {
      refetch();
      // A merge removes movements and moves their amounts, so patching a row
      // cannot express it: counts, totals and which rows still exist all
      // change. `refetch` only feeds the flat list, which the grouped view
      // switches off — hence the "it only showed after a reload".
      refreshGroupedView();
    }
  }, [refetch, refreshGroupedView, setDateRange, dateRange]);

  const handleSplitClick = useCallback((movement) => {
    setSplitMovement(movement);
    onSplitOpen();
  }, [onSplitOpen]);

  const handleSplitComplete = useCallback((movementId, movementDate) => {
    setSelectedIds(new Set());
    if (movementId) {
      setSelectedMovementId(movementId);
      pendingScrollId.current = movementId;
    }
    if (movementDate && !isDateWithinRange(movementDate, dateRange)) {
      setDateRange(getDateRangeForDate(movementDate, dateRange.mode));
    } else {
      refetch();
      // Splitting adds rows the grouped windows have never seen — same reason.
      refreshGroupedView();
    }
  }, [refetch, refreshGroupedView, setDateRange, dateRange]);

  const handleGroupComplete = useCallback((groupId, movementIds) => {
    patchRows(movementIds, { movement_group_id: groupId });
    setSelectedIds(new Set());
  }, [patchRows]);

  /**
   * Where a loaded movement is found, whichever view loaded it (rev 5). The
   * flat list answers when Grouping = None; the row store answers when the
   * rows came from a group, which is why merge/group/split used to open with
   * an empty selection.
   */
  const getRow = rowStore.get;
  const findMovement = useCallback(
    (id) => movementsList.find(m => m.id === id) || getRow(id),
    [movementsList, getRow]
  );

  // Get full movement objects for selected IDs
  const selectedMovements = useMemo(() => {
    if (selectedIds.size === 0) return [];
    return Array.from(selectedIds).map(findMovement).filter(Boolean);
  }, [selectedIds, findMovement]);

  const handleUngroupSelected = useCallback(async () => {
    const movsToUngroup = selectedMovements.filter(m => m.movement_group_id);
    if (movsToUngroup.length === 0) return;
    try {
      await Promise.all(
        movsToUngroup.map(m => updateMovement(m.id, { movement_group_id: 0 }))
      );
      patchRows(movsToUngroup.map(m => m.id), { movement_group_id: null });
      setSelectedIds(new Set());
      toast({
        title: t('list.toasts.ungroupSuccess.title'),
        description: t(movsToUngroup.length === 1 ? 'list.toasts.ungroupSuccess.descriptionOne' : 'list.toasts.ungroupSuccess.descriptionOther', { count: movsToUngroup.length }),
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: t('list.toasts.ungroupError.title'),
        description: t('list.toasts.ungroupError.description'),
        status: 'error',
        duration: 4000,
      });
    }
  }, [selectedMovements, patchRows, toast, t]);

  // Bulk category change for all selected movements
  const handleBulkCategoryChange = useCallback(async (category) => {
    try {
      await Promise.all(
        Array.from(selectedIds).map(id => updateMovement(id, { category_id: category.id }))
      );
      patchRows(Array.from(selectedIds), { category, category_id: category.id });
      refreshGroupsIfDimension(GROUPING_CATEGORY);
      toast({ title: t('list.toasts.bulkCategoryUpdated', { name: category.name, count: selectedIds.size }), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('list.toasts.bulkCategoryFailed'), status: 'error', duration: 3000 });
    }
  }, [selectedIds, patchRows, refreshGroupsIfDimension, toast, t]);

  /**
   * Bulk "Set goal". The endpoint attaches what the rules allow and reports
   * the rest, so the toast says both — with each skipped movement's reason in
   * words, because "2 skipped" on its own is not actionable.
   */
  const handleBulkGoal = useCallback(async (goal) => {
    const ids = Array.from(selectedIds);
    try {
      const result = await attachMovementsToGoal(goal.id, ids);
      const skipped = result.skipped || [];
      patchRows(result.attached || [], { goal: { id: goal.id, name: goal.name, color: goal.color, icon: goal.icon, effect: 'none' } });
      // The rows' effect and every goal header figure come from the server, so
      // the grouped view is re-read whatever it groups by, not only by goal.
      refreshGroupedView();
      refetchGoals();
      refetch();
      toast({
        title: i18n.t('goals:bulk.result', { attached: (result.attached || []).length, name: goal.name, skipped: skipped.length }),
        description: skipped.length ? (
          <Box>
            {skipped.map(item => (
              <Text key={item.movement_id} fontSize="xs">
                {getPrimaryText(findMovement(item.movement_id)) || `#${item.movement_id}`}
                {' — '}
                {i18n.t(`goals:reasons.${item.reason}`, { defaultValue: item.reason })}
              </Text>
            ))}
          </Box>
        ) : undefined,
        status: skipped.length ? 'warning' : 'success',
        duration: skipped.length ? 8000 : 2500,
        isClosable: true,
      });
    } catch {
      toast({ title: i18n.t('goals:toasts.saveFailed'), status: 'error', duration: 3000 });
    }
  }, [selectedIds, patchRows, refreshGroupedView, refetchGoals, refetch, findMovement, toast]);

  const handleBulkGoalRemove = useCallback(async () => {
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map(id => updateMovement(id, { goal_id: null })));
      patchRows(ids, { goal: null });
      refreshGroupedView();
      refetchGoals();
      refetch();
      toast({ title: i18n.t('goals:bulk.removed', { count: ids.length }), status: 'success', duration: 2500 });
    } catch {
      toast({ title: i18n.t('goals:toasts.saveFailed'), status: 'error', duration: 3000 });
    }
  }, [selectedIds, patchRows, refreshGroupedView, refetchGoals, refetch, toast]);

  // Bulk tag toggle for all selected movements
  const handleBulkTagToggle = useCallback(async (tag) => {
    try {
      for (const id of selectedIds) {
        const mov = findMovement(id);
        if (!mov) continue;
        const currentTagIds = (mov.tag_movements || []).map(tm => tm.tag?.id || tm.tag_id);
        const hasTag = currentTagIds.includes(tag.id);
        const newTagIds = hasTag
          ? currentTagIds.filter(tid => tid !== tag.id)
          : [...currentTagIds, tag.id];
        await updateMovement(id, { tag_ids: newTagIds });
        const newTagMovements = newTagIds.map(tid => {
          const t = allTags.find(at => at.id === tid);
          return { tag_id: tid, movement_id: id, tag: t };
        });
        patchRows([id], { tag_movements: newTagMovements });
      }
      toast({ title: t('list.toasts.bulkTagsUpdated', { count: selectedIds.size }), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('list.toasts.tagsUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [selectedIds, findMovement, allTags, patchRows, toast, t]);

  // MI-315 — what an empty list says, the same in every view. With a filter
  // or the toolbar's search narrowing it, the movements exist and just do not
  // match; with nothing narrowing it, the onboarding invitation to upload a
  // statement (MI-297) is the useful answer.
  const hasFilters = (apiFilters || []).length > 0 || apiFilterGroups.length > 0
    || Boolean(searchText.trim());
  const emptyState = hasFilters ? (
    <Text fontSize="sm" color="redesign.text.muted" textAlign="center" py={10}>
      {t('display.empty')}
    </Text>
  ) : (
    <OnboardingEmptyState
      titleKey="empty.movements.title"
      subtitleKey="empty.movements.subtitle"
      actions={[
        { label: 'empty.movements.upload', primary: true, onClick: () => navigate('/source-files') },
      ]}
    />
  );

  // MI-315 — the list's "select all". Read off the ref at render: a stale
  // answer only ever shows "some" for a moment where "all" is true, and the
  // next render corrects it.
  const showSelectAll = display.view !== VIEW_BOARD
    && (display.isGrouped ? movementGroups?.length > 0 : movementsList.length > 0);
  const renderedIds = [...renderedRowsRef.current.keys()];
  const allOnScreenSelected = renderedIds.length > 0 && renderedIds.every(id => selectedIds.has(id));

  // URL movement_id sync helpers
  const updateMovementIdInUrl = useCallback((movementId) => {
    const url = new URL(window.location);
    if (movementId != null) {
      url.searchParams.set('movement_id', movementId);
    } else {
      url.searchParams.delete('movement_id');
    }
    window.history.replaceState({}, '', url);
    detailPanelMoved();
  }, []);

  // Panel handlers
  const handleOpenPanel = useCallback((movementId) => {
    setSelectedMovementId(movementId);
    updateMovementIdInUrl(movementId);
  }, [updateMovementIdInUrl]);

  const handleClosePanel = useCallback(() => {
    setSelectedMovementId(null);
    updateMovementIdInUrl(null);
  }, [updateMovementIdInUrl]);

  // A link to a movement followed while this page is already open (a row in
  // the floating chat, MI-343). The id is in the address, but it is only read
  // from there on the first render.
  const location = useLocation();
  const followedLocation = useRef(location.key);
  useEffect(() => {
    if (followedLocation.current === location.key) return;
    followedLocation.current = location.key;
    const linked = Number(new URLSearchParams(location.search).get('movement_id'));
    if (linked > 0) handleOpenPanel(linked);
  }, [location.key, location.search, handleOpenPanel]);

  // Scroll to a movement element by ID after DOM renders.
  // Uses querySelectorAll because mobile + desktop versions share the same ID;
  // picks the first one that is actually visible (not display:none).
  // Grouped rows load after their group does, so it keeps looking for ~3s.
  // `instant` jumps rather than animates: a link lands on its row at once.
  const scrollToMovement = useCallback((movementId, { instant = false } = {}) => {
    let attemptsLeft = 10;
    const tryScroll = () => {
      const candidates = document.querySelectorAll(`#movement-${movementId}`);
      const el = Array.from(candidates).find(e => e.offsetParent !== null) || candidates[0];
      if (el) {
        el.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'center' });
        // A layout shift mid-animation can stop it short; settle it once.
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          if (el.isConnected && (r.top < 0 || r.bottom > window.innerHeight)) {
            el.scrollIntoView({ block: 'center' });
          }
        }, 800);
      } else if (--attemptsLeft > 0) {
        setTimeout(tryScroll, 300);
      }
    };
    setTimeout(tryScroll, 300);
  }, []);

  // Pending scroll: set by URL restore, merge, or split to trigger scroll after data loads
  const pendingScrollId = useRef(null);
  const hasRestoredFromUrl = useRef(false);

  // MI-315 — the list on screen has its data. The flat query is off while a
  // grouping is active (the default view, and the board), so waiting on it
  // alone meant a movement_id link never opened the panel there.
  const listLoaded = display.isGrouped
    ? !groupsLoading && movementGroups?.length > 0
    : !loading && movementsList.length > 0;

  // Grouped by day, the link's row can sit past the days loaded so far (15 per
  // page, newest first). Its date says how far to page, so it is fetched right
  // away, alongside the first page, rather than once that page is in.
  const seeksByDate = display.grouping === GROUPING_DAY && display.view !== VIEW_BOARD;
  const [urlMovementDate, setUrlMovementDate] = useState(null);  // { id, date }
  const [urlSeekId, setUrlSeekId] = useState(null);
  const urlDateRequested = useRef(false);
  useEffect(() => {
    if (urlDateRequested.current || hasRestoredFromUrl.current || !initialMovementId || !seeksByDate) return;
    urlDateRequested.current = true;
    fetchMovementById(initialMovementId)
      .then(m => {
        if (m?.date) setUrlMovementDate({ id: initialMovementId, date: String(m.date).slice(0, 10) });
      })
      .catch(() => {});
  }, [initialMovementId, seeksByDate]);

  // Restore movement_id from URL on initial load
  useEffect(() => {
    if (hasRestoredFromUrl.current || !listLoaded || !initialMovementId) return;
    hasRestoredFromUrl.current = true;
    setSelectedMovementId(initialMovementId);
    updateMovementIdInUrl(initialMovementId);
    if (!display.isGrouped) {
      pendingScrollId.current = initialMovementId;
    } else if (seeksByDate) {
      setUrlSeekId(initialMovementId);
    }
  }, [listLoaded, initialMovementId, updateMovementIdInUrl, display.isGrouped, seeksByDate]);

  // Load days until the link's one is in, then jump to its row. Once the
  // loaded days reach past its date without it, it is not in this list (a
  // filter, the range) and the search ends; the panel shows it all the same.
  useEffect(() => {
    if (!urlSeekId || urlMovementDate?.id !== urlSeekId || groupsLoading || groupsLoadingMore) return;
    if (selectedMovementId !== urlSeekId) {
      setUrlSeekId(null);  // the panel was closed, or moved to another movement
      return;
    }
    const target = urlMovementDate.date;
    const days = (movementGroups || []).map(g => String(g.value).slice(0, 10));
    if (days.includes(target)) {
      setUrlSeekId(null);
      scrollToMovement(urlSeekId, { instant: true });
      return;
    }
    const oldestLoaded = days.reduce((oldest, day) => (day < oldest ? day : oldest), days[0]);
    if (hasMoreGroups && oldestLoaded && target < oldestLoaded) {
      loadMoreGroups();
    } else {
      setUrlSeekId(null);
    }
  }, [urlSeekId, urlMovementDate, groupsLoading, groupsLoadingMore, movementGroups,
      hasMoreGroups, loadMoreGroups, selectedMovementId, scrollToMovement]);

  // Scroll to movement after data finishes loading
  useEffect(() => {
    if (!listLoaded || !pendingScrollId.current) return;
    scrollToMovement(pendingScrollId.current);
    pendingScrollId.current = null;
  }, [listLoaded, movementsList, movementGroups, scrollToMovement]);

  // A shared link that filters by goal falls back to the rest of its filters
  // for someone with Goals off, the way a goal grouping falls back to Day.
  useEffect(() => {
    if (goalsEnabled) return;
    const index = selectedFilters.findIndex(f => f.entity === 'Goal');
    if (index !== -1) removeFilter(index);
  }, [goalsEnabled, selectedFilters, removeFilter]);

  const visibleFilterOptions = useMemo(
    () => (goalsEnabled
      ? filterOptions
      : (filterOptions || []).filter(option => option.entity !== 'Goal')),
    [filterOptions, goalsEnabled],
  );

  const suggestionsWithoutGoals = useCallback(async (...args) => {
    const result = await fetchSuggestions(...args);
    if (goalsEnabled || !result?.suggestions) return result;
    return { ...result, suggestions: result.suggestions.filter(s => s.entity !== 'Goal') };
  }, [fetchSuggestions, goalsEnabled]);

  const replaceRow = rowStore.replace;
  /**
   * A saved edit reaches the list without reloading it. The row on screen is
   * swapped for what the server saved, and the list is read again only when
   * the edit can change what it is made of: a date, an amount, a category
   * moves a row between groups and changes the headers' figures, while a note
   * or a tag changes nothing but the row. A filter can turn any field into one
   * of those — it decides which rows belong — so with one active the headers
   * are always checked. The check itself is one quiet read, and only the
   * groups whose numbers moved read their rows.
   *
   * `saved` is what the panel already fetched; without it (a delete, an
   * ignore) the row is looked up, or the list read again.
   */
  const handleMovementUpdated = useCallback(async (saved) => {
    const id = saved?.id ?? selectedMovementId;
    // A new movement, or nothing to say which one changed.
    if (!id || id === PANEL_CREATE_MODE) {
      refetch();
      return;
    }
    if (!display.isGrouped) {
      if (saved) patchMovements([id], saved);
      if (!saved || hasFilters
          || movementShapesLists(movementsList.find(m => m.id === id), saved)) {
        refetch({ quiet: true });
      }
      return;
    }
    const before = getRow(id);
    let after = saved;
    if (!after) {
      try {
        after = await fetchMovementById(id);
      } catch {
        /* the row keeps what it had; the headers below correct the rest */
      }
    }
    if (after) replaceRow(after);
    if (!after || hasFilters || movementShapesLists(before, after)) refreshGroupHeaders();
  }, [refetch, display.isGrouped, selectedMovementId, replaceRow, getRow, patchMovements,
      movementsList, hasFilters, refreshGroupHeaders]);


  const isPanelOpen = selectedMovementId !== null;

  /**
   * The row layout follows the list's own width (MI-336).
   *
   * It used to follow `isPanelOpen`, which was a fair proxy while the panel
   * was always 35%: open meant cramped. With a panel the reader can drag, the
   * two stopped being the same question — a panel pulled down to its minimum
   * leaves the list wider than it ever was with the panel closed, and it would
   * still have been drawing the stacked mobile row.
   */
  const [
    listRef, listIsNarrow, categoryAsIcon, hideGoalMark, hideReconciled,
    hideMovementDate, hideCreatedDate, accountAsIcon,
  ] = useNarrowerThan(LIST_ROW_COLUMN_STEPS);

  /**
   * Properties the Display menu should stop offering (MI-336).
   *
   * The row sheds columns as the list narrows, and a chip for a column the
   * row has already dropped is a switch that does nothing. The dates and the
   * reconciled mark qualify; the account and the category do not, because
   * they are *reduced* to their icons rather than removed, so their chips
   * still turn something on and off.
   *
   * The property keeps its stored state while its chip is away — widen the
   * window and the chip comes back the way the reader left it.
   */
  const unavailableProperties = useMemo(() => [
    ...(hideMovementDate ? ['date'] : []),
    ...(hideCreatedDate ? ['created'] : []),
    ...(hideReconciled ? ['reconciled'] : []),
    ...(hideGoalMark || !goalsEnabled ? ['goal'] : []),
  ], [hideMovementDate, hideCreatedDate, hideReconciled, hideGoalMark, goalsEnabled]);

  /**
   * Whether the page still has room for both. Measured on the container rather
   * than the list, because the list's own width is downstream of the answer.
   */
  const [splitRef, cannotSplit, placeholderCrowdsList] =
    useNarrowerThan(ROW_SPLIT_THRESHOLDS);
  const panelTakesTheRow = cannotSplit && isPanelOpen;
  const listTakesTheRow = placeholderCrowdsList && !isPanelOpen;

  // MI-336 — the panel's width is the reader's to set, and it only occupies
  // the page while it is open. It resets to the default every time it opens
  // fresh (closed → open) rather than remembering the last drag.
  const {
    containerRef: panelWidthRef, isResizing, startResize, onHandleKeyDown, resetWidth,
  } = useResizablePanel(isPanelOpen);

  // While a movement is open its panel says how wide it is, so the floating
  // chat stands beside it (MI-343). The empty placeholder says nothing: the
  // chat may cover that.
  const panelHandleRef = useRef(null);
  usePublishDetailPanel(panelHandleRef, isPanelOpen);

  // Both hooks want the same node: one publishes the width onto it, the other
  // watches how wide it is.
  const setRowContainer = useCallback((node) => {
    panelWidthRef.current = node;
    splitRef.current = node;
  }, [panelWidthRef, splitRef]);

  // MI-311: hand the already-loaded row to the panel so opening a movement
  // doesn't re-request what the list just fetched.
  const selectedMovementRow = useMemo(
    () => findMovement(selectedMovementId) || null,
    // `rowStore.version` is what makes an edited row reach the panel; without
    // it this memo would hand back the pre-edit object it captured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findMovement, selectedMovementId, rowStore.version]
  );

  return (
    <Flex
      // Carries the panel's width as a CSS custom property. The drag writes it
      // here directly, so resizing costs a layout instead of a re-render of
      // every row in the list beside it (see useResizablePanel).
      ref={setRowContainer}
      sx={{ [PANEL_WIDTH_VAR]: `${PANEL_DEFAULT_WIDTH}px` }}
      // Ends above the assistant bar (MI-315). The layout reserves its height
      // below the page, so a full 100vh ran 48px under the fixed bar: the
      // bottom of the list and of the panel, the panel's Save bar included,
      // hid behind it until the whole document was scrolled. On a computer
      // the content panel says how much room that leaves (--mk-page-h).
      h={{ base: "calc(100vh - 56px)", md: "var(--mk-page-h)" }}
      overflow="hidden"
      position="relative"
      mt={{ base: "-15px", md: 0 }}
    >
      {/* Left side: Movement List. It takes whatever the panel is not using
          (MI-336) — a fixed 65% left a third of the page empty whenever the
          panel was closed, and could not follow a panel the reader resizes. */}
      <Box
        ref={listRef}
        flex="1"
        minW={0}
        // Out of the way once the page is too narrow to hold both and a
        // movement is open: the panel is what the reader is looking at.
        display={{ base: "block", md: panelTakesTheRow ? "none" : "block" }}
        overflowY="auto"
        overflowX="hidden"
        px={{ base: 2, md: 4 }}
      >
      {/* ── Header: toolbar + pagination ── */}
      <Box
        bg="redesign.bg.page"
        mx={{ base: -2, md: -4 }}
        px={{ base: 2, md: 4 }}
        mb={3}
        position="sticky"
        top={0}
        zIndex={10}
        borderBottom="1px solid"
        borderBottomColor="redesign.border"
      >
      <FilterBar
        showDisplayCurrency={false}
        dateRange={dateRange}
        onNavigatePeriod={navigatePeriod}
        onModeChange={setDateMode}
        onCustomRange={setCustomRange}
        onToday={goToToday}
        filterOptions={visibleFilterOptions}
        filterOptionsLoading={filterOptionsLoading}
        selectedFilters={selectedFilters}
        onAddFilter={addFilter}
        onAppendFilter={appendFilter}
        onRemoveFilter={removeFilter}
        onUpdateFilter={updateFilter}
        onClearFilters={clearFilters}
        timePresets={timePresets}
        isAdvancedMode={isAdvancedMode}
        filterGroups={filterGroups}
        groupsOperator={groupsOperator}
        onEnableAdvancedMode={enableAdvancedMode}
        onDisableAdvancedMode={disableAdvancedMode}
        onAddFilterGroup={addFilterGroup}
        onRemoveFilterGroup={removeFilterGroup}
        onToggleGroupOperator={toggleGroupOperator}
        onToggleGroupsOperator={toggleGroupsOperator}
        onAddGroupCondition={addGroupCondition}
        onRemoveGroupCondition={removeGroupCondition}
        searchText={searchText}
        onSearchChange={setSearchText}
        onSearchSubmit={handleSearchSubmit}
        filterPlacement="start"
        onApplySuggestion={applySuggestion}
        onRemoveLastFilter={removeLastFilter}
        fetchSuggestions={suggestionsWithoutGoals}
        onAiFilter={applyAiFilter}
        onParsePeriod={applyAiPeriod}
      >
        {/* MI-139 — its own button, next to Filter but independent of it */}
        <DisplayMenu
          settings={display}
          currencyCount={visibleCurrencyCount}
          unavailableProperties={unavailableProperties}
        />
        {canCreate && (
          <IconButton
            {...TOOLBAR_ICON_BUTTON}
            icon={<AddIcon boxSize={3} />}
            onClick={() => setSelectedMovementId(PANEL_CREATE_MODE)}
            aria-label={t('list.toolbar.newMovement')}
          />
        )}
      </FilterBar>

      {/* How many movements the current filters matched — Linear keeps this
          count next to the list. It counts rows, not money: the grand total was
          deliberately removed (MI-318) and stays removed. */}
      {(showSelectAll || matchedCount != null) && (
        <Flex align="center" gap={2} py={1}>
          {/* MI-315 — beside the count, aligned with the rows' checkboxes. The
              board draws cards without checkboxes, so it has none. */}
          {showSelectAll && (
            <Tooltip label={t(selectedIds.size > 0 ? 'list.bulkBar.clearSelection' : 'list.bulkBar.selectAll')} fontSize="xs" hasArrow openDelay={300}>
              <Box pl={3} display="flex" alignItems="center">
                <SelectionCheckbox
                  isChecked={allOnScreenSelected}
                  isIndeterminate={selectedIds.size > 0 && !allOnScreenSelected}
                  onChange={handleToggleSelectAll}
                  ariaLabel={t(selectedIds.size > 0 ? 'list.bulkBar.clearSelection' : 'list.bulkBar.selectAll')}
                />
              </Box>
            </Tooltip>
          )}
          {matchedCount != null && (
            <Text fontSize="xs" color="redesign.text.muted">
              {t('list.pagination.matched', { count: matchedCount })}
            </Text>
          )}
        </Flex>
      )}

      {/* MI-318: no filtered-set total here. The grouped headers already carry
          the figures that matter, and a second grand total on top of them read as
          noise. The unconvertible disclosure stays — it is the one thing the
          totals payload says that the headers cannot. */}
      {displayCurrency && convertedTotal?.num_unconvertible > 0 && (
        <Flex align="center" justify="flex-end" py={2}>
          <UnconvertibleNote
            buckets={convertedTotal.unconvertible}
            count={convertedTotal.num_unconvertible}
            justify="flex-end"
          />
        </Flex>
      )}

      </Box>

      {/* Loading state */}
      {!display.isGrouped && loading && (
        <Flex justify="center" align="center" py={12}>
          <Text fontSize="xl" color="redesign.text.muted">{t('list.states.loading')}</Text>
        </Flex>
      )}

      {/* Error state */}
      {error && (
        <Flex justify="center" align="center" py={12}>
          <Text fontSize="xl" color="redesign.accent.red">{error}</Text>
        </Flex>
      )}

      {/* MI-139 — grouped view. Replaces the flat day-grouped list while a
          grouping is active; Grouping = None falls straight back through. */}
      {display.isGrouped && !error && (
        <GroupedMovements
          groups={movementGroups}
          swimlanes={movementSwimlanes}
          generation={groupsGeneration}
          groupVersions={groupVersions}
          loading={groupsLoading}
          emptyContent={emptyState}
          settings={display}
          // rev 5 — every window a group fetches is registered with the page,
          // and every window it renders is resolved back through it.
          onRowsLoaded={rowStore.register}
          resolveRows={rowStore.resolve}
          groupMeta={groupMeta}
          // MI-139: grouped rows render through the very same component the
          // ungrouped list uses, so they keep selection, inline category
          // editing, tags and alerts rather than reimplementing a thinner row.
          // Passed down rather than imported to avoid a circular import.
          renderRows={(movements) => (
            <MovementList
              isVisible={display.isVisible}
              movements={movements}
              onEditClick={handleOpenPanel}
              stacked={listIsNarrow}
              accountAsIcon={accountAsIcon}
              hideMovementDate={hideMovementDate}
              hideCreatedDate={hideCreatedDate}
              categoryAsIcon={categoryAsIcon}
              hideReconciled={hideReconciled}
              hideGoalMark={hideGoalMark || !goalsEnabled}
              selectedIds={selectedIds}
              onSelect={handleToggleSelect}
              selectionActive={selectionActive}
              highlightedId={selectedMovementId}
              categories={categories}
              onCategoryChange={handleCategoryChange}
              allTags={allTags}
              onTagsChange={handleTagsChange}
              onRendered={handleRowsRendered}
            />
          )}
          filters={queryFilters}
          filterGroups={apiFilterGroups}
          groupsOperator={groupsOperator}
          startDate={dateRange.start_date}
          endDate={dateRange.end_date}
          onSelectMovement={handleOpenPanel}
          hasMoreGroups={hasMoreGroups}
          loadingMore={groupsLoadingMore}
          onLoadMoreGroups={loadMoreGroups}
        />
      )}

      {/* MI-336 — the ungrouped list: no date headers, one continuous run of
          rows in the order the Display menu names. Grouping by day is what
          draws the day-by-day view now, and it is the default. */}
      {!display.isGrouped && !loading && !error && (
        <>
          {movementsList.length > 0 && (
            <Box pt={1}>
              <MovementList
                movements={movementsList}
                onEditClick={handleOpenPanel}
                isVisible={display.isVisible}
                stacked={listIsNarrow}
                accountAsIcon={accountAsIcon}
                hideMovementDate={hideMovementDate}
                hideCreatedDate={hideCreatedDate}
                categoryAsIcon={categoryAsIcon}
                hideReconciled={hideReconciled}
                hideGoalMark={hideGoalMark || !goalsEnabled}
                selectedIds={selectedIds}
                onSelect={handleToggleSelect}
                selectionActive={selectionActive}
                highlightedId={selectedMovementId}
                categories={categories}
                onCategoryChange={handleCategoryChange}
                allTags={allTags}
                onTagsChange={handleTagsChange}
                onRendered={handleRowsRendered}
              />
            </Box>
          )}

          {/* One gesture for "more", the same one the grouped view and the
              board use. The hint carries what the page numbers used to say
              without being asked: how much of the whole is on screen. */}
          {hasMore && movementsList.length > 0 && (
            <Box pb={4} pt={1}>
              <LoadMoreButton
                onClick={loadMore}
                isLoading={loadingMore}
                label={t('list.pagination.loadMore')}
                hint={paginationInfo.totalInRange
                  ? t('list.pagination.showing', {
                      shown: movementsList.length,
                      total: paginationInfo.totalInRange,
                    })
                  : null}
              />
            </Box>
          )}

          {movementsList.length === 0 && emptyState}
        </>
      )}


      </Box>

      {/* Right side: the detail panel. It keeps its column whether or not a
          movement is selected — closing one returns the placeholder rather
          than handing the width to the list, which kept the row's columns from
          having to lay themselves out twice over wildly different widths. What
          the reader controls is its *size*: the handle below narrows it, and
          opening a movement starts it back at the default. On mobile it stays
          an overlay, so it only exists there while something is selected. */}
      <Box
          display={{
            base: isPanelOpen ? "block" : "none",
            // A placeholder should not outrank the list for the last of the
            // width: with nothing selected and no room for both, the panel
            // stands down.
            md: listTakesTheRow ? "none" : "block",
          }}
          position={{ base: "absolute", md: "relative" }}
          right={{ base: 0, md: "auto" }}
          top={{ base: 0, md: "auto" }}
          flex="none"
          /*
           * The width the reader dragged, capped by what the list can spare —
           * in CSS, so it follows a window resize without a render. Read from
           * the inside out: never below the panel's own floor, never wider
           * than what leaves the list its minimum, and otherwise whatever the
           * reader asked for. Full-width on mobile, where the panel is an
           * overlay, and full-width again once the page is too narrow to hold
           * both and a movement is open.
           */
          w={{
            base: "100%",
            md: panelTakesTheRow
              ? "100%"
              // An open movement may take the list down to its minimum; an
              // empty placeholder may not take it below its full width.
              : `max(${PANEL_MIN_WIDTH}px, min(var(${PANEL_WIDTH_VAR}), calc(100% - ${isPanelOpen ? LIST_MIN_WIDTH : LIST_ACCOUNT_ICON_WIDTH}px)))`,
          }}
          h="100%"
          bg="redesign.bg.page"
          borderLeft="1px solid"
          borderLeftColor="redesign.border"
          zIndex={{ base: 10, md: "auto" }}
        >
          {/* The drag handle sits in the border, out of the panel's flow, so
              nothing inside has to make room for it. Invisible until you reach
              for it — a permanent grip line on every screen is furniture. */}
          <Box
            ref={panelHandleRef}
            display={{ base: "none", md: panelTakesTheRow ? "none" : "block" }}
            position="absolute"
            left="-3px"
            top={0}
            bottom={0}
            w="6px"
            cursor="col-resize"
            zIndex={2}
            role="separator"
            aria-orientation="vertical"
            aria-label={t('list.detailPanel.resize')}
            tabIndex={0}
            onPointerDown={startResize}
            onKeyDown={onHandleKeyDown}
            onDoubleClick={resetWidth}
            _hover={{ bg: "redesign.accent.blue", opacity: 0.35 }}
            _focusVisible={{ bg: "redesign.accent.blue", opacity: 0.6, outline: "none" }}
            bg={isResizing ? "redesign.accent.blue" : "transparent"}
            opacity={isResizing ? 0.6 : 1}
            transition="background 0.12s, opacity 0.12s"
          />
          {isPanelOpen ? (
            <MovementDetailPanel
              movementId={selectedMovementId === PANEL_CREATE_MODE ? null : selectedMovementId}
              initialMovement={selectedMovementRow}
              onClose={handleClosePanel}
              onMovementUpdated={handleMovementUpdated}
              onSplitClick={handleSplitClick}
              onSelectMovement={handleOpenPanel}
            />
          ) : (
            <Flex direction="column" align="center" justify="center" gap={3} h="100%" bg="redesign.bg.page" color="redesign.text.faint">
              <Box as={LuLayers} boxSize="32px" />
              <Text fontSize="sm" color="redesign.text.muted">{t('list.detailPanel.placeholder')}</Text>
            </Flex>
          )}
        </Box>

      {/* Floating action bar for multi-select — Copilot Money style */}
      {selectedIds.size > 0 && (
        <Flex {...cardSurface}
          position="fixed"
          // Sit above the assistant dock bar so it never hides the actions.
          bottom={`${DOCK_H + 12}px`}
          left="50%"
          transform="translateX(-50%)"
          borderRadius="full"
          px={2}
          py={1.5}
          align="center"
          gap={1}
          boxShadow="0 2px 16px rgba(0,0,0,0.12)"
          maxW="calc(100vw - 16px)"
          zIndex={20}
        >
          {/* X + count */}
          <Flex align="center" gap={1} px={2}>
            <IconButton
              icon={<CloseIcon boxSize="8px" />}
              size="xs"
              variant="ghost"
              color="redesign.text.muted"
              _hover={{ bg: "redesign.bg.card" }}
              onClick={handleClearSelection}
              aria-label={t('list.bulkBar.clearSelection')}
              borderRadius="full"
              minW="24px"
              h="24px"
            />
            <Text fontSize="sm" fontWeight="500" color="redesign.text" whiteSpace="nowrap">
              {t('list.bulkBar.selectedCount', { count: selectedIds.size })}
            </Text>
            <Box w="1px" h="20px" bg="redesign.border" mx={2} />
            <SelectionTotal movements={selectedMovements} />
          </Flex>
          <Box w="1px" h="20px" bg="redesign.border" mx={1} />

          {/* Category picker */}
          <Popover placement="top" isLazy>
            {({ onClose: onCatClose }) => (
              <>
                <PopoverTrigger>
                  <Button size="sm" variant="ghost" color="redesign.text" _hover={{ bg: "redesign.bg.card" }} borderRadius="full" leftIcon={<LuFolder size={14} />} iconSpacing={BULK_ICON_SPACING} fontWeight="500" fontSize="sm" px={3} aria-label={t('list.bulkBar.category')}>
                    <Box as="span" display={BULK_LABEL_DISPLAY}>{t('list.bulkBar.category')}</Box>
                  </Button>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent w="220px" boxShadow="lg" borderRadius="xl" border="1px solid" borderColor="redesign.border">
                    <PopoverBody p={2}>
                      <BulkCategoryPicker categories={categories} selectedMovements={selectedMovements} onSelect={(cat) => { handleBulkCategoryChange(cat); onCatClose(); }} />
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </>
            )}
          </Popover>

          {/* Tag picker */}
          <Popover placement="top" isLazy>
            <PopoverTrigger>
              <Button size="sm" variant="ghost" color="redesign.text" _hover={{ bg: "redesign.bg.card" }} borderRadius="full" leftIcon={<LuTag size={14} />} iconSpacing={BULK_ICON_SPACING} fontWeight="500" fontSize="sm" px={3} aria-label={t('list.bulkBar.tag')}>
                <Box as="span" display={BULK_LABEL_DISPLAY}>{t('list.bulkBar.tag')}</Box>
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent w="240px" boxShadow="lg" borderRadius="xl" border="1px solid" borderColor="redesign.border">
                <PopoverBody p={2}>
                  <BulkTagPicker tags={allTags} selectedMovements={selectedMovements} onToggle={handleBulkTagToggle} />
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>

          {/* Goal picker — only for a user who turned Goals on */}
          {goalsEnabled && can('goal.update') && (
            <Popover placement="top" isLazy>
              {({ onClose: onGoalClose }) => (
                <>
                  <PopoverTrigger>
                    <Button size="sm" variant="ghost" color="redesign.text" _hover={{ bg: "redesign.bg.card" }} borderRadius="full" leftIcon={<LuTarget size={14} />} iconSpacing={BULK_ICON_SPACING} fontWeight="500" fontSize="sm" px={3} aria-label={i18n.t('goals:bulk.setGoal')}>
                      <Box as="span" display={BULK_LABEL_DISPLAY}>{i18n.t('goals:bulk.setGoal')}</Box>
                    </Button>
                  </PopoverTrigger>
                  <Portal>
                    <PopoverContent w="260px" boxShadow="lg" borderRadius="xl" border="1px solid" borderColor="redesign.border">
                      <PopoverBody p={2}>
                        <BulkGoalPicker
                          accounts={accounts}
                          onSelect={(goal) => { handleBulkGoal(goal); onGoalClose(); }}
                          onRemove={() => { handleBulkGoalRemove(); onGoalClose(); }}
                        />
                      </PopoverBody>
                    </PopoverContent>
                  </Portal>
                </>
              )}
            </Popover>
          )}

          {/* More actions (three dots) */}
          <Menu placement="top">
            <MenuButton
              as={IconButton}
              icon={<LuEllipsis size={16} />}
              size="sm"
              variant="ghost"
              color="redesign.text.muted"
              _hover={{ bg: "redesign.bg.card" }}
              borderRadius="full"
              aria-label={t('list.bulkBar.moreActions')}
              minW="32px"
            />
            <Portal>
              <MenuList minW="160px" boxShadow="lg" borderRadius="xl" border="1px solid" borderColor="redesign.border" py={1}>
                <MenuItem fontSize="sm" onClick={onGroupOpen} isDisabled={selectedIds.size < 2}>
                  {t('list.bulkBar.group', { count: selectedIds.size })}
                </MenuItem>
                <MenuItem fontSize="sm" onClick={handleUngroupSelected} isDisabled={!selectedMovements.some(m => m.movement_group_id)}>
                  {t('list.bulkBar.ungroup')}
                </MenuItem>
                <MenuItem fontSize="sm" onClick={onMergeOpen} isDisabled={selectedIds.size < 2}>
                  {t('list.bulkBar.merge', { count: selectedIds.size })}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>
      )}

      {/* Merge modal */}
      <MergeMovementsModal
        isOpen={isMergeOpen}
        onClose={onMergeClose}
        movements={selectedMovements}
        onMergeComplete={handleMergeComplete}
      />

      {/* Split modal */}
      <SplitMovementModal
        isOpen={isSplitOpen}
        onClose={onSplitClose}
        movement={splitMovement}
        onSplitComplete={handleSplitComplete}
      />

      {/* Group modal */}
      <GroupMovementsModal
        isOpen={isGroupOpen}
        onClose={onGroupClose}
        movements={selectedMovements}
        onGroupComplete={handleGroupComplete}
      />

    </Flex>
  );
}
